/**
 * Copyright (c) 2024-2026 Danielle Sage. All rights reserved.
 * Proprietary and confidential. Unauthorised use, reproduction or distribution
 * of this software, in whole or in part, is strictly prohibited.
 * See LICENSE.txt for full terms.
 */

import { useState, useEffect } from "react";
import { supabase } from "./supabase";
import { buildVqfFormModel, VQF_FORM_TITLES } from "./vqfFormLayout";
import { generateFormConfirmationPDF } from "./generateFormConfirmationPDF";
import { exportCaseFile } from "./exportCaseFile";

// Readable labels for Sumsub Form A/K questionnaire fields — mirrors the MLRO dashboard maps
// so Jaybee sees the same human-readable declaration when viewing a margin-48 confirmation.
const SUMSUB_FIELD_LABELS = {
  formKEstablishingOfT: 'Form K — Controlling Person',
  formAOfTheBeneficial: 'Form A — Beneficial Owner',
  pleaseChooseLegalEnt: 'Legal entity type',
  firstNames: 'First name(s)',
  lastNames: 'Last name(s)',
  dateOfBirth: 'Date of birth',
  nationality: 'Nationality',
  actualAddressOfDomic: 'Address of domicile/residency',
  fiduciaryQuestionLab: 'Fiduciary holding assets (third party beneficial owner)',
  pleaseConfirmThatYou: 'Confirmed — Art. 251 SCC false declaration warning',
  informationUpdateObl: 'Information Update Obligation confirmed',
  question1: 'Declaration basis (25%+ / other control / managing director)',
};
const SUMSUB_VALUE_LABELS = {
  holding25: 'Holds 25%+ of shares or voting rights',
  controllingOther: 'Controls in another recognisable way',
  managingDirector: 'Managing director (fallback — no controlling person identifiable)',
  operating: 'Operating company',
  domiciliary: 'Domiciliary company',
  yes: 'Yes',
  no: 'No',
  confirmed: '✓ Confirmed',
};
const sumsubFieldLabel = (id) => SUMSUB_FIELD_LABELS[id] || id;
const sumsubValueLabel = (val) => SUMSUB_VALUE_LABELS[val] || val;

export default function JaybeeReview() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [review, setReview] = useState(null);
  const [session, setSession] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [decision, setDecision] = useState(null);
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [decidedBy, setDecidedBy] = useState("");
  const [verifications, setVerifications] = useState([]);
  const [walletScreenings, setWalletScreenings] = useState([]);
  const [sowSessions, setSowSessions] = useState([]);
  const [sowDocs, setSowDocs] = useState({});
  const [expandedSow, setExpandedSow] = useState({});
  const [jayCommandInput, setJayCommandInput] = useState('');
  const [jayCommandLoading, setJayCommandLoading] = useState(false);
  const [jayCommandHistory, setJayCommandHistory] = useState([]);
  const [showDetail, setShowDetail] = useState(false);
  const [caseNotes, setCaseNotes] = useState([]);
  const [showNotes, setShowNotes] = useState(false);
  const [sumsubFormRequests, setSumsubFormRequests] = useState([]);
  const [docTrackingRows, setDocTrackingRows] = useState([]);
  // Form A/K confirmation viewer (Sumsub margin-48 route) — { record, data?, error?, loading? }
  const [formConfirmView, setFormConfirmView] = useState(null);
  const [exportingCaseFile, setExportingCaseFile] = useState("");

  const viewFormConfirmation = async (record) => {
    setFormConfirmView({ record, loading: true });
    try {
      const params = new URLSearchParams({ action: 'form-answers', applicantId: record.applicant_id || '' });
      if (record.applicant_action_id) params.set('applicantActionId', record.applicant_action_id);
      const res = await fetch(`/api/sumsub?${params.toString()}`);
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Failed to fetch the confirmed declaration');
      setFormConfirmView({ record, data });
    } catch (e) {
      setFormConfirmView({ record, error: e.message });
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("token");
    if (!token) { setError("Invalid review link."); setLoading(false); return; }
    loadReview(token);
  }, []);

  const loadReview = async (token) => {
    try {
      const { data: reviews, error: re } = await supabase.from("jaybee_reviews").select("*").eq("token", token).single();
      if (re || !reviews) { setError("Review not found or link has expired."); setLoading(false); return; }
      if (reviews.status === "superseded") {
        setError("This review link has been superseded. Sage Capital Management has sent you an updated link — please use the most recent email.");
        setLoading(false);
        return;
      }
      if (reviews.status !== "pending") {
        setReview(reviews);
        setSubmitted(true);
        setLoading(false);
        return;
      }
      if (new Date(reviews.expires_at) < new Date()) { setError("This review link has expired. Please contact Sage Capital Management."); setLoading(false); return; }
      setReview(reviews);

      const { data: sess } = await supabase.from("onboarding_sessions").select("*").eq("id", reviews.session_id).single();
      setSession(sess);

      const { data: docs } = await supabase.from("onboarding_documents").select("*").eq("session_id", reviews.session_id).order("created_at");
      setDocuments(docs || []);
      const { data: verifs } = await supabase.from("sumsub_verifications").select("*").eq("session_id", reviews.session_id);
      setVerifications(verifs || []);
      const { data: wallets } = await supabase.from("wallet_screenings").select("*").eq("session_id", reviews.session_id);
      setWalletScreenings(wallets || []);
      const { data: notes } = await supabase.from("case_notes").select("*").eq("session_id", reviews.session_id).order("created_at", { ascending: true });
      setCaseNotes(notes || []);
      // Sumsub margin-48 Form A/K confirmations — the sole route for VQF forms.
      const { data: ssForms } = await supabase.from("sumsub_form_requests").select("*").eq("session_id", reviews.session_id).order("created_at", { ascending: false });
      setSumsubFormRequests(ssForms || []);
      // MLRO document-requirement decisions (Covered by other documents / Not required) — these
      // drive the Supporting Documents matrix so waived items don't show as Outstanding to Jaybee.
      const { data: dt } = await supabase.from("document_tracking").select("*").eq("session_id", reviews.session_id);
      setDocTrackingRows(dt || []);
      // Build AML checklist from session data
      setSowSessions(prev => prev); // trigger after session loaded

      // Fetch SOW sessions for UBOs
      const { data: sowData } = await supabase.from("onboarding_sessions")
        .select("*")
        .in("status", ["sow_complete", "sow_interview"])
        .order("created_at");
      // Filter to those linked to this parent session
      const linkedSow = (sowData || []).filter(s => 
        s.collected_data?.parentSessionId === reviews.session_id ||
        (session?.contact_email && s.contact_email?.toLowerCase() === session.contact_email?.toLowerCase())
      );
      setSowSessions(linkedSow);
      // Fetch SOW documents for each
      for (const sow of linkedSow) {
        const { data: sd } = await supabase.from("onboarding_documents").select("*").eq("session_id", sow.id).order("created_at");
        setSowDocs(prev => ({ ...prev, [sow.id]: sd || [] }));
      }

    } catch(e) {
      setError("Unable to load review: " + e.message);
    }
    setLoading(false);
  };

  const submitDecision = async (dec) => {
    if (!decidedBy.trim()) { alert("Please enter your name before submitting."); return; }
    if ((dec === "info" || dec === "decline") && !notes.trim()) { alert("Please add notes explaining your decision."); return; }
    setSubmitting(true);
    try {
      const res = await fetch("/api/jaybee-decision", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: review.token, decision: dec, notes, decidedBy })
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error);
      setDecision(dec);
      setSubmitted(true);
    } catch(e) {
      alert("Error: " + e.message);
    }
    setSubmitting(false);
  };

  const getSignedUrl = async (filePath) => {
    const { data } = await supabase.storage.from("onboarding-documents").createSignedUrl(filePath, 3600);
    if (data?.signedUrl) window.open(data.signedUrl, "_blank");
  };

  // Open the Sumsub identity report for a verification row. Uses the stored copy if
  // present, otherwise fetches it on demand. Resolves historical rows via externalUserId.
  const openSumsubReport = async (v) => {
    if (!v) return;
    try {
      let path = v.report_path;
      if (!path) {
        const r = await fetch("/api/sumsub-report", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ sessionId: v.session_id, applicantId: v.applicant_id, externalUserId: v.external_user_id }) });
        const j = await r.json().catch(() => ({}));
        path = j.path;
        if (!path && j.error) { alert("Sumsub report could not be retrieved:\n\n" + j.error); return; }
      }
      if (!path) { alert("The Sumsub report is not available yet for this person."); return; }
      const { data, error } = await supabase.storage.from("onboarding-documents").createSignedUrl(path, 300);
      if (data?.signedUrl) { window.open(data.signedUrl, "_blank"); return; }
      alert("Could not open the Sumsub report." + (error ? "\n\n" + error.message : ""));
    } catch (e) { alert("Could not open the Sumsub report: " + (e.message || e)); }
  };

  // Open the AML screening report (adverse media / sanctions / PEP), rendered on demand.
  const openAmlReport = async (v, personName, personRole) => {
    if (!v) return;
    const win = window.open("", "_blank");
    if (win) { try { win.document.write('<!doctype html><meta charset="utf-8"><title>AML screening report</title><body style="margin:0;font:16px -apple-system,BlinkMacSystemFont,Segoe UI,Roboto,sans-serif;color:#334155;padding:28px">Generating the AML screening report...</body>'); } catch (_) {} }
    try {
      const r = await fetch("/api/sumsub-aml", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ sessionId: v.session_id, applicantId: v.applicant_id, externalUserId: v.external_user_id, personName, personRole }) });
      const j = await r.json().catch(() => ({}));
      if (j.html) {
        if (win) { try { win.document.open(); win.document.write(j.html); win.document.close(); } catch (_) {} }
        else { window.location.href = URL.createObjectURL(new Blob([j.html], { type: "text/html;charset=utf-8" })); }
      } else {
        if (win) win.close();
        alert("AML screening report could not be retrieved:\n\n" + (j.error || "Unknown error"));
      }
    } catch (e) { if (win) win.close(); alert("Could not open the AML screening report: " + (e.message || e)); }
  };

  // Wrap an async open action with pressed-state feedback on the button.
  const withPressFeedback = (fn) => async (ev) => {
    const btn = ev.currentTarget;
    const orig = btn.textContent;
    btn.textContent = "Opening...";
    btn.disabled = true;
    btn.style.opacity = "0.6";
    try { await fn(); } finally { btn.textContent = orig; btn.disabled = false; btn.style.opacity = "1"; }
  };

  const REPORT_STATUSES = ["approved", "approved_with_flags", "rejected", "requires_review"];

  const S = {
    page: { minHeight: "100vh", background: "#f8fafc", fontFamily: "Georgia, serif", color: "#111827" },
    header: { background: "#1e2d5a", padding: "20px 40px", display: "flex", justifyContent: "space-between", alignItems: "center" },
    container: { maxWidth: 900, margin: "0 auto", padding: "32px 24px" },
    section: { background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8, padding: "24px 28px", marginBottom: 20 },
    sectionTitle: { fontSize: 11, fontWeight: 700, color: "#1e2d5a", textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: 16, paddingBottom: 8, borderBottom: "2px solid #1e2d5a" },
    kv: { display: "flex", gap: 8, marginBottom: 8 },
    kvLabel: { fontSize: 12, color: "#6b7280", minWidth: 140, flexShrink: 0, fontFamily: "Arial, sans-serif" },
    kvVal: { fontSize: 13, color: "#111827", fontFamily: "Arial, sans-serif", lineHeight: 1.5 },
    para: { fontSize: 13, color: "#374151", lineHeight: 1.8, fontFamily: "Arial, sans-serif", marginBottom: 8 },
    badge: (color) => ({ display: "inline-block", padding: "3px 10px", borderRadius: 12, fontSize: 11, fontWeight: 700, background: color + "20", border: "1px solid " + color + "40", color }),
    docRow: { display: "flex", alignItems: "center", gap: 12, padding: "10px 12px", borderBottom: "1px solid #f3f4f6" },
    btn: (bg, color) => ({ padding: "12px 28px", background: bg, color, border: "none", borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: "Arial, sans-serif" }),
  };

  if (loading) return (
    <div style={{ ...S.page, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ textAlign: "center", fontFamily: "Arial, sans-serif" }}>
        <div style={{ fontSize: 32, marginBottom: 12 }}>⏳</div>
        <div style={{ color: "#6b7280" }}>Loading review file...</div>
      </div>
    </div>
  );

  if (error) return (
    <div style={{ ...S.page, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ textAlign: "center", maxWidth: 400, fontFamily: "Arial, sans-serif" }}>
        <div style={{ fontSize: 32, marginBottom: 12 }}>⚠️</div>
        <div style={{ color: "#dc2626", marginBottom: 8, fontWeight: 600 }}>{error}</div>
        <div style={{ color: "#6b7280", fontSize: 13 }}>Please contact the compliance team at compliance@sagecapital.co.uk</div>
      </div>
    </div>
  );

  if (submitted) return (
    <div style={{ ...S.page, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ textAlign: "center", maxWidth: 500, fontFamily: "Arial, sans-serif" }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>{review?.status === "approved" || decision === "approve" ? "✅" : review?.status === "declined" || decision === "decline" ? "❌" : "📋"}</div>
        <div style={{ fontSize: 20, fontWeight: 700, color: "#1e2d5a", marginBottom: 8 }}>
          {decision === "approve" || review?.status === "approved" ? "Approval Recorded" : decision === "decline" || review?.status === "declined" ? "Decline Recorded" : "Information Requested"}
        </div>
        <div style={{ color: "#6b7280", fontSize: 14, lineHeight: 1.7 }}>
          {decision === "approve" ? "Thank you. Your approval has been recorded and Sage Capital Management has been notified. They will proceed with final client onboarding." :
           decision === "decline" ? "Thank you. Your decision has been recorded and Sage Capital Management has been notified." :
           decision === "info" ? "Thank you. Your request has been sent to Sage Capital Management. They will be in touch with the additional information you have requested." :
           "This review has already been completed."}
        </div>
        {review?.decision_notes && <div style={{ marginTop: 16, padding: "12px 16px", background: "#f9fafb", borderRadius: 8, fontSize: 13, color: "#374151", textAlign: "left" }}><strong>Your notes:</strong> {review.decision_notes}</div>}
      </div>
    </div>
  );

  // Safely coerce any report array item to a string.
  // The AI occasionally wraps string values in objects e.g. {note:"..."} — this guards all renders.
  const toStr = v => {
    if (!v) return "";
    if (typeof v === "string") return v;
    if (typeof v === "object") return v.note || v.text || v.content || v.value || v.flag || v.finding || JSON.stringify(v);
    return String(v);
  };

  const report = session?.report || {};
  const cd = report.companyDetails || {};
  const fc = report.finsaClassification || {};
  const finsaCategory = typeof fc === "object" ? (fc.category || "—") : String(fc || "—");
  const finsaBasis = typeof fc === "object" ? fc.basis : null;
  const riskColor = (report.riskTier || "").toLowerCase().includes("high") ? "#dc2626" : (report.riskTier || "").toLowerCase().includes("medium") ? "#d97706" : "#16a34a";

  // ----- Decision-ready model (additive redesign) -----------------------------
  const fmt = (...xs) => xs.map(toStr).filter(x => x && x !== "...").join(" · ");
  const ap = report.activityProfile || {};
  const entityType = toStr(report.entityProfile?.entityType) || toStr(report.clientType) || "—";
  const mlroRec = toStr(report.mlroRecommendation) || session?.mlro_decision || "Approve";
  const recNorm = (mlroRec || "").toLowerCase();
  const recApprove = recNorm.includes("approve");
  const recDecline = recNorm.includes("decline");
  const recColor = recApprove ? "#16a34a" : recDecline ? "#dc2626" : "#d97706";
  const recBg = recApprove ? "#f0fdf4" : recDecline ? "#fef2f2" : "#fff7ed";
  const reviewRef = (review?.id || session?.id || "").toString().slice(0, 8).toUpperCase();

  const ubos25List = (report.ubos || []).filter(u => parseFloat(String(u.ownership).match(/[\d.]+/)?.[0] || "0") >= 25);
  const sumsubApproved = verifications.filter(v => v.status === "approved").length;
  const hasCrypto = walletScreenings.length > 0;
  const treasuryName = fc.treasuryContactName;

  // Mirror the dashboard: key verifications by exact person_name, with a normalised fallback,
  // and drive identity status from report.sumsubRequired (directors + UBOs + signatories).
  const verifByName = {};
  verifications.forEach(v => { if (v?.person_name) verifByName[v.person_name] = v; });
  const _norm = (s) => String(s || "").toLowerCase().replace(/\s+/g, " ").trim();
  const verifFor = (name) => {
    if (!name) return null;
    if (verifByName[name]) return verifByName[name];
    const t = _norm(name); if (!t) return null;
    const exact = verifications.find(v => _norm(v.person_name) === t); if (exact) return exact;
    const parts = t.split(" ").filter(Boolean); const first = parts[0], last = parts[parts.length - 1];
    return verifications.find(v => { const n = _norm(v.person_name); return first && last && n.includes(first) && n.includes(last); })
      || verifications.find(v => { const n = _norm(v.person_name); return first && n.includes(first); }) || null;
  };
  const reqPeople = (report.sumsubRequired || []).map(p => typeof p === "object" ? p : { name: String(p).split(" — ")[0], role: String(p).split(" — ")[1] || "Director / UBO" });
  const reqApproved = reqPeople.filter(p => verifFor(p.name)?.status === "approved").length;
  // Entity screening results. The dashboard stores the RAW OpenSanctions result in
  // collected_data.entitySanctions (not the {status, data} wrapper it uses in local state),
  // so the stored shape must be normalised here exactly as the dashboard normalises it on
  // load — including deriving clear_with_notes when every match has been dismissed as a
  // false positive. Additional manually screened entities (counterparties etc.) are stored
  // separately in collected_data.additionalSanctionsScreenings and are surfaced here too.
  const normaliseScreening = (raw) => {
    if (!raw) return null;
    if (raw.status && !raw.entityChecked) return raw; // already a {status, data} wrapper
    if (raw.keyRequired) return { status: "no_key" };
    if (raw.error) return { status: "error" };
    const dismissed = raw.dismissed || {};
    const allMatches = [...(raw.hits || []), ...(raw.cautionMatches || [])];
    const allDismissed = allMatches.length > 0 && allMatches.every(h => dismissed[h.name]);
    if (allDismissed) return { status: "clear_with_notes", data: raw };
    if (raw.sanctioned) return { status: "hit", data: raw };
    if (raw.hasPartialMatches) return { status: "caution", data: raw };
    return { status: "clear", data: raw };
  };
  const entitySanctions = normaliseScreening(session?.collected_data?.entitySanctions);
  const additionalEntityScreenings = (session?.collected_data?.additionalSanctionsScreenings || []).map(a => {
    const n = (a.data && (a.data.entityChecked || a.data.hits || a.data.sanctioned !== undefined)) ? normaliseScreening(a.data) : null;
    return { name: a.name, checkedAt: a.checkedAt, status: n?.status || a.status, data: n?.data || a.data || {} };
  });

  // Form A / K completion status — Sumsub margin-48 route only (questionnaire + phone OTP).
  // The DeepSign QES route for VQF forms has been retired; any historical signed PDFs remain
  // visible as documents in the client file. Deduplicated per (form type, signer).
  const ssAK = (sumsubFormRequests || []).filter(r => (r.form_type === "A" || r.form_type === "K") && r.status !== "cancelled");
  const akMap = {};
  ssAK.forEach(r => { const k = r.form_type + "|" + _norm(r.signer_name); if (r.status === "confirmed") akMap[k] = "done"; else if (akMap[k] !== "done") akMap[k] = "pending"; });
  const akTotal = Object.keys(akMap).length;
  const akDone = Object.values(akMap).filter(v => v === "done").length;
  const formAKState = akTotal === 0 ? "none" : (akDone === akTotal ? "signed" : "sent");
  const akDoneLabels = [...new Set(ssAK.filter(r => r.status === "confirmed").map(r => "Form " + r.form_type + " (Sumsub questionnaire + OTP)"))];
  const formAKEvidence = formAKState === "none" ? "Not yet issued"
    : formAKState === "signed" ? "Completed: " + akDoneLabels.join(", ")
    : "Sent — awaiting confirmation (" + akDone + " of " + akTotal + " completed)";

  // MLRO tracking decisions from document_tracking, looked up by EXACT item_name — identical to
  // the dashboard, which keys its tracking map on the gap key verbatim. The gap key is derived
  // exactly as the dashboard derives it, so a decision recorded there is honoured here 1:1.
  const gapKeyOf = (gap) => typeof gap === "string" ? gap : (gap.documentType || gap.description || gap.gap || JSON.stringify(gap));
  const trackingFor = (key) => docTrackingRows.find(t => t.item_name === key) || null;

  // Requirements coverage — mirrors the Sage / Jaybee Legal Entity Questionnaire headings.
  const coverage = [
    { label: "Object & purpose of the relationship",
      ok: !!(report.objectAndPurpose?.object && report.objectAndPurpose?.purpose),
      evidence: (report.objectAndPurpose?.object || report.objectAndPurpose?.purpose)
        ? [report.objectAndPurpose?.object, report.objectAndPurpose?.purpose].filter(Boolean).join(" · ")
        : "Not yet documented (Art. 6 AMLA) — see Object & Purpose section" },
    { label: "Entity sanctions screening (company)",
      ok: ["clear", "clear_with_notes"].includes(entitySanctions?.status) && !additionalEntityScreenings.some(a => a.status === "hit"),
      evidence: (entitySanctions
        ? (entitySanctions.status === "clear" || entitySanctions.status === "clear_with_notes"
            ? "OpenSanctions — " + (entitySanctions.status === "clear_with_notes" ? "clear (false positives dismissed)" : "no match") + " for " + toStr(entitySanctions.data?.entityChecked || cd.legalName) + (entitySanctions.data?.checkedAt ? " · " + new Date(entitySanctions.data.checkedAt).toLocaleDateString("en-GB") : "")
            : entitySanctions.status === "hit" ? "⚠ Sanctions hit — see Entity Sanctions Screening in section 3"
            : entitySanctions.status === "caution" ? "Partial match under review — see section 3"
            : "Screening in progress")
        : "Not yet run")
        + (additionalEntityScreenings.length > 0
            ? " · Additional entities: " + additionalEntityScreenings.map(a => toStr(a.name) + " (" + (a.status === "hit" ? "⚠ HIT" : a.status === "caution" ? "partial match" : a.status === "clear_with_notes" ? "clear, notes" : a.status || "—") + ")").join(", ")
            : "") },
    { label: "Company information",
      ok: !!(cd.legalName && cd.registrationNumber && cd.jurisdiction),
      partial: !!(cd.legalName && !(cd.registrationNumber && cd.jurisdiction)),
      evidence: fmt(cd.legalName || session?.company_name, cd.jurisdiction, cd.registrationNumber ? "Reg " + cd.registrationNumber : "") || "Not confirmed" },
    { label: "Business activity & places of business",
      ok: !!cd.businessDescription,
      evidence: (toStr(cd.businessDescription).slice(0, 120)) || "Not described" },
    { label: "Financial circumstances (turnover / assets)",
      ok: !!ap.annualTurnover,
      partial: !ap.annualTurnover && !!(ap.currencies || ap.monthlyTransactionVolume),
      evidence: fmt(
        ap.annualTurnover ? "Turnover " + toStr(ap.annualTurnover) : "",
        ap.totalAssets ? "Assets " + toStr(ap.totalAssets) : "",
        ap.currencies ? toStr(ap.currencies) : "",
        report.sofAssessment?.namedAccount ? "SoF " + toStr(report.sofAssessment.namedAccount) : "") || "Turnover / assets band not captured" },
    { label: "Authority to act / signatories",
      ok: (report.authorisedSignatories || []).length > 0 || (report.directors || []).length > 0,
      evidence: (report.authorisedSignatories || []).map(s => toStr(s.name || s)).filter(Boolean).join(", ")
        || (report.directors || []).map(d => toStr(d.name || d)).filter(Boolean).join(", ") || "Not confirmed" },
    { label: "Entity type",
      ok: entityType !== "—",
      evidence: entityType !== "—" ? entityType : "Not classified" },
    { label: "VQF Form A / K (confirmed — Sumsub OTP)",
      ok: formAKState === "signed",
      partial: formAKState === "sent",
      evidence: formAKEvidence },
    { label: "Professional treasury",
      ok: !!treasuryName,
      na: !treasuryName && fc.mlroEquivalenceCheck === false && !fc.treasuryContactEmail,
      evidence: treasuryName ? "Qualified person: " + fmt(treasuryName, fc.treasuryContactTitle) : "Not declared / not applicable" },
    { label: "Tax compliance declaration",
      ok: !!report.taxCompliance?.declared && !report.taxCompliance?.riskIndicatorsPresent,
      partial: !!report.taxCompliance?.riskIndicatorsPresent,
      evidence: report.taxCompliance?.riskIndicatorsPresent ? "Risk indicators present — see file"
        : report.taxCompliance?.declared ? "Declaration obtained" : "Not yet recorded" },
    { label: "Identity verification (Sumsub)",
      ok: reqPeople.length > 0 ? reqApproved === reqPeople.length : (verifications.length > 0 && sumsubApproved === verifications.length),
      partial: reqPeople.length > 0 ? (reqApproved > 0 && reqApproved < reqPeople.length) : (sumsubApproved > 0 && sumsubApproved < verifications.length),
      evidence: reqPeople.length ? reqApproved + " of " + reqPeople.length + " individuals verified" : (verifications.length ? sumsubApproved + " of " + verifications.length + " verified" : "Not yet completed") },
    { label: "Beneficial owners & controllers",
      ok: (report.ubos || []).length > 0,
      evidence: (report.ubos || []).map(u => fmt(u.name, u.ownership ? toStr(u.ownership) : "")).filter(Boolean).join("; ") || "Not identified" },
    { label: "Source of wealth (UBOs ≥ 25%)",
      ok: !!report.sowWaiver || ubos25List.length === 0 || ubos25List.every(u => u.sowAssessment || u.sowInterviewComplete),
      na: !!report.sowWaiver || ubos25List.length === 0,
      evidence: report.sowWaiver ? "Not required — " + toStr(report.sowWaiver.reason) + (report.sowWaiver.by ? " (MLRO waiver: " + report.sowWaiver.by + (report.sowWaiver.at ? ", " + new Date(report.sowWaiver.at).toLocaleDateString("en-GB") : "") + ")" : "")
        : ubos25List.length === 0 ? "No UBO at 25%+ threshold"
        : ubos25List.map(u => fmt(u.name, (u.sowAssessment || u.sowInterviewComplete) ? "assessed" : "pending")).join("; ") },
    { label: "Wallet screening & Travel Rule",
      ok: !hasCrypto || report.travelRule?.overallStatus === "compliant",
      na: !hasCrypto,
      partial: hasCrypto && report.travelRule?.overallStatus !== "compliant",
      evidence: !hasCrypto ? "No crypto wallets declared"
        : walletScreenings.length + " wallet(s) screened · Travel Rule " + (report.travelRule?.overallStatus === "compliant" ? "compliant" : "pending") },
  ];
  const covStatus = (c) => c.na ? "na" : c.ok ? "complete" : c.partial ? "partial" : "outstanding";
  const coverageActive = coverage.filter(c => !c.na);
  const completeCount = coverageActive.filter(c => covStatus(c) === "complete").length;
  const outstandingCount = coverageActive.filter(c => covStatus(c) !== "complete").length;

  // Supporting-documents matrix — mirrors Jaybee's supporting-documentation checklist.
  // Render MLRO case notes readably: preserve line breaks, bold **labels**, lay out
  // - bullets and 1. numbered conditions. The MLRO enters these in a textarea with
  // markdown-style structure; a plain div collapses it into one block.
  const formatNotes = (text) => {
    if (!text) return null;
    const renderInline = (line) => line.split(/(\*\*[^*]+\*\*)/g).map((p, i) => {
      const m = p.match(/^\*\*([\s\S]+)\*\*$/);
      return m ? <strong key={i}>{m[1].trim()}</strong> : <span key={i}>{p}</span>;
    });
    return String(text).split(/\r?\n/).map((raw, i) => {
      const line = raw.trim();
      if (!line) return <div key={i} style={{ height: 6 }} />;
      const bullet = /^[-•]\s+/.test(line);
      const numbered = /^\d+\.\s+/.test(line);
      const content = bullet ? line.replace(/^[-•]\s+/, "") : line;
      return (
        <div key={i} style={{ display: "flex", gap: bullet ? 8 : 0, marginBottom: 5, paddingLeft: (bullet || numbered) ? 14 : 0, lineHeight: 1.6 }}>
          {bullet && <span style={{ color: "#9ca3af", flexShrink: 0 }}>•</span>}
          <span>{renderInline(content)}</span>
        </div>
      );
    });
  };

  return (
    <div style={S.page}>
      {/* Header */}
      <div style={S.header}>
        <div>
          <div style={{ fontSize: 11, color: "#93c5fd", letterSpacing: "1px", textTransform: "uppercase", marginBottom: 2 }}>Sage Capital Management</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: "#fff" }}>Client Acceptance — External Review</div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: 11, color: "#93c5fd" }}>Confidential — Jaybee Compliance only</div>
          <div style={{ fontSize: 11, color: "#93c5fd" }}>Expires: {review?.expires_at ? new Date(review.expires_at).toLocaleDateString("en-GB") : "—"}</div>
        </div>
      </div>

      <div style={S.container}>
        {/* Decision banner */}
        <div style={{ background: recBg, border: `2px solid ${recColor}`, borderRadius: 8, padding: "18px 24px", marginBottom: 16 }}>
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
            <div>
              <div style={{ fontSize: 11, color: recColor, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", fontFamily: "Arial, sans-serif" }}>Internal MLRO Recommendation</div>
              <div style={{ fontSize: 22, fontWeight: 700, color: recColor, fontFamily: "Arial, sans-serif", marginTop: 2 }}>{recApprove ? "APPROVE" : recDecline ? "DECLINE" : mlroRec}</div>
              <div style={{ fontSize: 13, color: "#374151", fontFamily: "Arial, sans-serif", marginTop: 6 }}>{fmt(cd.legalName || session?.company_name, cd.jurisdiction, entityType !== "—" ? entityType : "") || "—"}</div>
            </div>
            <div style={{ textAlign: "right", fontFamily: "Arial, sans-serif" }}>
              <div style={{ marginBottom: 6 }}><span style={S.badge(riskColor)}>{report.riskTier || "—"} risk</span></div>
              <div style={{ fontSize: 12, color: "#6b7280" }}>Reviewed by</div>
              <div style={{ fontSize: 14, fontWeight: 600, color: "#111827" }}>Ross Griffiths, Compliance Manager</div>
              <div style={{ fontSize: 12, color: "#6b7280" }}>{session?.approved_at ? new Date(session.approved_at).toLocaleDateString("en-GB") : "—"}{reviewRef ? " · Ref " + reviewRef : ""}</div>
            </div>
          </div>
          <div style={{ display: "flex", gap: 12, marginTop: 16, flexWrap: "wrap" }}>
            {[
              ["Requirements complete", `${completeCount} / ${coverageActive.length}`, completeCount === coverageActive.length ? "#16a34a" : "#d97706"],
              ["Documents on file", `${documents.length}`, "#1e2d5a"],
              ["Items outstanding", `${outstandingCount}`, outstandingCount === 0 ? "#16a34a" : "#d97706"],
            ].map(([label, val, col]) => (
              <div key={label} style={{ flex: 1, minWidth: 140, background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8, padding: "12px 16px" }}>
                <div style={{ fontSize: 22, fontWeight: 700, color: col, fontFamily: "Arial, sans-serif" }}>{val}</div>
                <div style={{ fontSize: 11, color: "#6b7280", fontFamily: "Arial, sans-serif", textTransform: "uppercase", letterSpacing: "0.4px", marginTop: 2 }}>{label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* MLRO conditions */}
        {session?.mlro_notes && (
          <div style={{ ...S.section, borderLeft: "4px solid #1e2d5a" }}>
            <div style={S.sectionTitle}>MLRO Conditions & Notes</div>
            <div style={{ ...S.para, marginBottom: 0 }}>{formatNotes(session.mlro_notes)}</div>
          </div>
        )}

        {/* Website Pre-Screening */}
        {(report.websiteFindings?.url && report.websiteFindings?.summary && report.websiteFindings.summary !== "...") && (
          ((report.websiteFindings.redFlags || []).filter(f => f && f !== "...").length > 0 ||
           (report.websiteFindings.contradictions || []).filter(c => c && c !== "...").length > 0) && (
          <div style={{ ...S.section, borderLeft: "4px solid #dc2626" }}>
            <div style={S.sectionTitle}>Website Pre-Screening</div>
            <div style={{ ...S.para, marginBottom:10 }}>{toStr(report.websiteFindings.summary)}</div>
            {(report.websiteFindings.redFlags || []).filter(f => f && f !== "...").map((f, i) => (
              <div key={i} style={{ fontSize:12, color:"#dc2626", fontFamily:"Arial, sans-serif", lineHeight:1.6, marginBottom:4 }}>🚩 {toStr(f)}</div>
            ))}
            {(report.websiteFindings.contradictions || []).filter(c => c && c !== "...").map((c, i) => (
              <div key={i} style={{ fontSize:12, color:"#d97706", fontFamily:"Arial, sans-serif", lineHeight:1.6, marginBottom:4 }}>⚠ {toStr(c)}</div>
            ))}
            {report.websiteFindings.mlroNote && (
              <div style={{ marginTop:10, padding:"10px 14px", background:"#f0fdf4", border:"1px solid #bbf7d0", borderRadius:6 }}>
                <div style={{ fontSize:10, color:"#166534", fontWeight:700, textTransform:"uppercase", letterSpacing:"0.5px", fontFamily:"Arial, sans-serif", marginBottom:4 }}>✓ MLRO Mitigation Note</div>
                <div style={{ fontSize:12, color:"#14532d", fontFamily:"Arial, sans-serif", lineHeight:1.6 }}>{toStr(report.websiteFindings.mlroNote)}</div>
              </div>
            )}
            {!report.websiteFindings.mlroNote && (
              <div style={{ marginTop:10, padding:"8px 12px", background:"#fff7ed", border:"1px solid #fed7aa", borderRadius:6, fontSize:12, color:"#92400e", fontFamily:"Arial, sans-serif" }}>
                ⚠ No MLRO mitigation note recorded for this website discrepancy.
              </div>
            )}
          </div>
          )
        )}

        {/* Requirements coverage — centrepiece */}
                {/* Case-file export — Art. 63 VQF Regulations: printable paper-equivalent dossier */}
        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
          <button
            disabled={!!exportingCaseFile}
            onClick={async () => {
              try {
                const { problems } = await exportCaseFile({
                  supabase, session, report,
                  documents: documents || [], verifications: verifications || [], caseNotes: caseNotes || [],
                  sumsubFormRequests: sumsubFormRequests || [], walletScreenings: walletScreenings || [],
                  entitySanctions, additionalEntityScreenings: additionalEntityScreenings || [],
                  fieldLabel: sumsubFieldLabel, valueLabel: sumsubValueLabel,
                }, setExportingCaseFile);
                if (problems.length) alert("Case file downloaded. " + problems.length + " item(s) could not be included — see EXPORT-NOTES.txt inside the ZIP.");
              } catch (e) { alert("Case file export failed: " + e.message); setExportingCaseFile(""); }
            }}
            title="Download the complete dossier as a single ZIP — compliance report, documents, identity reports, Form A/K confirmations, case notes, and screening evidence (Art. 63 VQF Regulations)"
            style={{ padding: "8px 18px", background: exportingCaseFile ? "#9ca3af" : "#1e2d5a", color: "#fff", border: "none", borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: exportingCaseFile ? "default" : "pointer", fontFamily: "Arial, sans-serif" }}>
            {exportingCaseFile || "⬇ Download case file (ZIP)"}
          </button>
        </div>

        {/* Object & Purpose of the Business Relationship — Art. 6 AMLA */}
        {(() => {
          const oap = report.objectAndPurpose || {};
          const has = !!(oap.object || oap.purpose);
          return (
            <div style={S.section}>
              <div style={S.sectionTitle}>Object &amp; Purpose of the Business Relationship</div>
              <div style={{ fontSize: 11, color: "#9ca3af", fontFamily: "Arial, sans-serif", marginBottom: 10 }}>Art. 6 AMLA — nature and purpose of the requested business relationship</div>
              {has ? (
                <>
                  {oap.object && <div style={{ marginBottom: 8 }}><div style={{ fontSize: 11, color: "#1e2d5a", fontWeight: 700, fontFamily: "Arial, sans-serif", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 3 }}>Object — requested service</div><div style={{ fontSize: 13, color: "#111827", fontFamily: "Arial, sans-serif", lineHeight: 1.6 }}>{toStr(oap.object)}</div></div>}
                  {oap.purpose && <div><div style={{ fontSize: 11, color: "#1e2d5a", fontWeight: 700, fontFamily: "Arial, sans-serif", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 3 }}>Purpose — why the client seeks the service</div><div style={{ fontSize: 13, color: "#111827", fontFamily: "Arial, sans-serif", lineHeight: 1.6 }}>{toStr(oap.purpose)}</div></div>}
                  {(oap.by || oap.at) && <div style={{ fontSize: 10, color: "#9ca3af", fontFamily: "Arial, sans-serif", marginTop: 8 }}>Documented by {toStr(oap.by) || "MLRO"}{oap.at ? " · " + new Date(oap.at).toLocaleDateString("en-GB") : ""}</div>}
                </>
              ) : (
                <div style={{ fontSize: 12, color: "#92400e", background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 6, padding: "8px 12px", fontFamily: "Arial, sans-serif" }}>
                  Not yet documented in structured form for this file. The underlying information is captured in the business profile and anticipated activity sections below.
                </div>
              )}
            </div>
          );
        })()}

<div style={S.section}>
          <div style={S.sectionTitle}>Requirements Coverage</div>
          <div style={{ ...S.para, marginBottom: 12 }}>
            Each acceptance requirement from the Sage onboarding questionnaire, with its status and the supporting evidence on file. Amber or red items are genuine gaps for your attention.
          </div>
          <table style={{ width: "100%", borderCollapse: "collapse", border: "1px solid #e5e7eb", borderRadius: 8, overflow: "hidden" }}>
            <thead>
              <tr style={{ background: "#1e2d5a" }}>
                <th style={{ padding: "8px 12px", textAlign: "left", fontSize: 11, color: "white", fontFamily: "Arial, sans-serif", fontWeight: 700, width: 240 }}>Requirement</th>
                <th style={{ padding: "8px 12px", textAlign: "center", fontSize: 11, color: "white", fontFamily: "Arial, sans-serif", fontWeight: 700, width: 110 }}>Status</th>
                <th style={{ padding: "8px 12px", textAlign: "left", fontSize: 11, color: "white", fontFamily: "Arial, sans-serif", fontWeight: 700 }}>Evidence on file</th>
              </tr>
            </thead>
            <tbody>
              {coverage.map((c, i) => {
                const st = covStatus(c);
                const pill = st === "complete" ? ["#15803d", "✓ Complete"] : st === "partial" ? ["#d97706", "◐ Partial"] : st === "na" ? ["#9ca3af", "N / A"] : ["#dc2626", "✗ Outstanding"];
                return (
                  <tr key={i} style={{ background: i % 2 === 0 ? "#fff" : "#f9fafb", borderBottom: "1px solid #f3f4f6" }}>
                    <td style={{ padding: "9px 12px", fontSize: 13, color: "#111827", fontFamily: "Arial, sans-serif", fontWeight: 600 }}>{c.label}</td>
                    <td style={{ padding: "9px 12px", textAlign: "center" }}><span style={S.badge(pill[0])}>{pill[1]}</span></td>
                    <td style={{ padding: "9px 12px", fontSize: 12, color: (st === "complete" || st === "na") ? "#374151" : "#b45309", fontFamily: "Arial, sans-serif" }}>{c.evidence}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Supporting-documents matrix */}
        <div style={S.section}>
          <div style={S.sectionTitle}>Supporting Documents</div>
          <table style={{ width: "100%", borderCollapse: "collapse", border: "1px solid #e5e7eb", borderRadius: 8, overflow: "hidden" }}>
            <thead>
              <tr style={{ background: "#1e2d5a" }}>
                <th style={{ padding: "8px 12px", textAlign: "left", fontSize: 11, color: "white", fontFamily: "Arial, sans-serif", fontWeight: 700 }}>Document</th>
                <th style={{ padding: "8px 12px", textAlign: "center", fontSize: 11, color: "white", fontFamily: "Arial, sans-serif", fontWeight: 700, width: 120 }}>Status</th>
                <th style={{ padding: "8px 12px", textAlign: "left", fontSize: 11, color: "white", fontFamily: "Arial, sans-serif", fontWeight: 700, width: 160 }}>File / Date</th>
              </tr>
            </thead>
            <tbody>
              {(() => {
                // Case-driven matrix: (1) every document actually on file, titled by the AI's
                // classification; (2) fixed electronic rows (Sumsub ID, Form A/K when in play);
                // (3) outstanding items drawn ONLY from the report's own documentGaps, honouring
                // MLRO tracking decisions. Nothing is keyword-matched — a document can never show
                // Outstanding because of what its file happened to be called.
                const rows = [];
                documents.forEach(d => rows.push({ kind: "doc", d }));
                rows.push({ kind: "sumsub" });
                if (akTotal > 0) rows.push({ kind: "forms" });
                (report.documentGaps || []).forEach(g => rows.push({ kind: "gap", g }));

                const cellDoc = { padding: "9px 12px", fontSize: 13, fontFamily: "Arial, sans-serif", fontWeight: 600 };
                const cellFile = { padding: "9px 12px", fontSize: 11, color: "#6b7280", fontFamily: "Arial, sans-serif" };
                const viewBtn = { fontSize: 11, color: "#1e2d5a", background: "transparent", border: "1px solid #1e2d5a", borderRadius: 4, padding: "2px 8px", cursor: "pointer", fontFamily: "Arial, sans-serif", marginRight: 6 };

                return rows.map((row, i) => {
                  const zebra = { background: i % 2 === 0 ? "#fff" : "#f9fafb", borderBottom: "1px solid #f3f4f6" };

                  if (row.kind === "doc") {
                    const d = row.d;
                    const title = toStr(d.document_type || d.document_name || "Document");
                    const fileName = toStr(d.document_name || "");
                    const assessment = (d.assessment || "").toLowerCase();
                    const pill = assessment === "unsatisfactory" ? ["#dc2626", "✗ Unsatisfactory"]
                      : assessment === "requires clarification" ? ["#d97706", "◐ Requires clarification"]
                      : ["#15803d", "✓ Received"];
                    return (
                      <tr key={"doc" + i} style={zebra}>
                        <td style={{ ...cellDoc, color: "#111827" }}>
                          {title}
                          {fileName && fileName !== title && <div style={{ fontSize: 10, color: "#9ca3af", fontWeight: 400, marginTop: 2 }}>{fileName}</div>}
                        </td>
                        <td style={{ padding: "9px 12px", textAlign: "center" }}><span style={S.badge(pill[0])}>{pill[1]}</span></td>
                        <td style={cellFile}>
                          {d.file_path ? <button onClick={() => getSignedUrl(d.file_path)} style={viewBtn}>View</button> : null}
                          {d.created_at ? new Date(d.created_at).toLocaleDateString("en-GB") : ""}
                        </td>
                      </tr>
                    );
                  }

                  if (row.kind === "sumsub") {
                    const met = reqPeople.length > 0 && reqApproved === reqPeople.length;
                    const partial = reqApproved > 0 && reqApproved < reqPeople.length;
                    const none = reqPeople.length === 0;
                    const pill = none ? ["#9ca3af", "N / A"] : met ? ["#15803d", "✓ Verified (Sumsub)"] : partial ? ["#d97706", "◐ Sumsub partial"] : ["#d97706", "Outstanding"];
                    return (
                      <tr key={"sumsub"} style={zebra}>
                        <td style={{ ...cellDoc, color: "#111827" }}>UBO / controller ID & proof of address</td>
                        <td style={{ padding: "9px 12px", textAlign: "center" }}><span style={S.badge(pill[0])}>{pill[1]}</span></td>
                        <td style={cellFile}>
                          {none ? "No individuals requiring verification listed"
                            : <span style={{ color: met ? "#15803d" : "#b45309" }}>Electronic — Sumsub ({reqApproved}/{reqPeople.length} individuals verified)</span>}
                          {reqPeople.map((p, pi) => {
                            const vm = verifications.find(v => v.person_name && p.name && v.person_name.toLowerCase().trim() === p.name.toLowerCase().trim());
                            if (!vm || !REPORT_STATUSES.includes(vm.status)) return null;
                            return (
                              <span key={pi} style={{ display: "block", marginTop: 3, color: "#374151" }}>
                                {p.name}{" "}
                                <button onClick={withPressFeedback(() => openSumsubReport(vm))} style={{ fontSize: 11, color: "#1e2d5a", background: "transparent", border: "1px solid #1e2d5a", borderRadius: 4, padding: "1px 8px", cursor: "pointer", fontFamily: "Arial, sans-serif", marginLeft: 4 }}>View identity report</button>
                              </span>
                            );
                          })}
                        </td>
                      </tr>
                    );
                  }

                  if (row.kind === "forms") {
                    const formMet = formAKState === "signed";
                    const pill = formMet ? ["#15803d", "✓ Confirmed"] : ["#d97706", "◐ Sent — awaiting"];
                    return (
                      <tr key={"forms"} style={zebra}>
                        <td style={{ ...cellDoc, color: "#111827" }}>VQF Form A / Form K</td>
                        <td style={{ padding: "9px 12px", textAlign: "center" }}><span style={S.badge(pill[0])}>{pill[1]}</span></td>
                        <td style={cellFile}>
                          <span style={{ color: formMet ? "#15803d" : "#b45309" }}>{formAKEvidence}</span>
                          {ssAK.filter(x => x.status === "confirmed").map((x, xi) => (
                            <span key={xi} style={{ display: "block", marginTop: 3, color: "#374151" }}>
                              Form {x.form_type} — {x.signer_name}{" "}
                              <button onClick={() => viewFormConfirmation(x)} style={{ ...viewBtn, padding: "1px 8px", marginLeft: 4, marginRight: 0 }}>View declaration</button>
                            </span>
                          ))}
                        </td>
                      </tr>
                    );
                  }

                  // Outstanding item from the report's own gap list — honours MLRO tracking decisions
                  const key = gapKeyOf(row.g);
                  const t = trackingFor(key);
                  const covered = t && t.status === "received";
                  const notRequired = t && (t.status === "waived" || t.status === "not_required");
                  const trackBy = t ? (t.updated_by || "").split("@")[0] : "";
                  const trackDate = t && t.updated_at ? new Date(t.updated_at).toLocaleDateString("en-GB") : "";
                  const pill = covered ? ["#15803d", "✓ Covered"] : notRequired ? ["#9ca3af", "N / A — not required"] : ["#d97706", "Outstanding"];
                  return (
                    <tr key={"gap" + i} style={zebra}>
                      <td style={{ ...cellDoc, color: notRequired ? "#9ca3af" : "#111827", textDecoration: notRequired ? "line-through" : "none" }}>{toStr(key)}</td>
                      <td style={{ padding: "9px 12px", textAlign: "center" }}><span style={S.badge(pill[0])}>{pill[1]}</span></td>
                      <td style={cellFile}>
                        {covered ? <span style={{ color: "#15803d" }}>{t.waiver_reason ? toStr(t.waiver_reason) : "Confirmed on file by MLRO"}{trackBy ? " — " + trackBy : ""}{trackDate ? ", " + trackDate : ""}</span>
                          : notRequired ? <span style={{ color: "#9ca3af" }}>{t.waiver_reason ? toStr(t.waiver_reason) : "Marked not required by MLRO"}{trackBy ? " — " + trackBy : ""}{trackDate ? ", " + trackDate : ""}</span>
                          : "—"}
                      </td>
                    </tr>
                  );
                });
              })()}
            </tbody>
          </table>
          <div style={{ fontSize: 11, color: "#9ca3af", fontFamily: "Arial, sans-serif", marginTop: 8 }}>
            Individual identity documents are verified electronically via Sumsub; per-person verification status is in the full file detail below.
          </div>
        </div>

        {/* Progressive disclosure toggle */}
        <button onClick={() => setShowDetail(s => !s)} style={{ width: "100%", padding: "12px 16px", background: showDetail ? "#1e2d5a" : "#fff", color: showDetail ? "#fff" : "#1e2d5a", border: "1px solid #1e2d5a", borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "Arial, sans-serif", marginBottom: 20 }}>
          {showDetail ? "▲ Hide full file detail" : "▼ Show full file detail — profile, ownership, source of funds & wealth, documents, sign-off"}
        </button>

        {showDetail && (<>
        {/* Six Questions + Procedural Requirements */}
        {session && (() => {
          const r = report;
          const sumsubOk = verifications.some(v => v.status === "approved");
          const allDocsOk = documents.length > 0 && documents.every(d => d.assessment !== "Unsatisfactory");

          const sixQuestions = [
            {
              q: "Is the company legitimate?",
              detail: r.companyDetails?.legalName ? `${r.companyDetails.legalName} · ${r.companyDetails.jurisdiction || ''} · Reg: ${r.companyDetails.registrationNumber || 'see file'}` : "Not confirmed",
              ok: !!(r.companyDetails?.legalName && r.companyDetails?.registrationNumber)
            },
            {
              q: "Are they regulated?",
              detail: r.regulatoryStatus?.regulated ? `${r.regulatoryStatus.regulator} — ${r.regulatoryStatus.licenceNumber || 'licence on file'}` : r.regulatoryStatus?.licenceType ? `Unregulated — ${r.regulatoryStatus.licenceType}. ${r.regulatoryStatus.assessment || ''}` : "Unregulated — see MLRO rationale",
              ok: !!(r.regulatoryStatus?.regulated || r.regulatoryStatus?.assessment)
            },
            {
              q: "Who is the UBO?",
              detail: (r.ubos || []).map(u => `${u.name} ${u.ownership}% (${u.nationality}, ${u.countryOfResidence || ''})`).join('; ') || "Not identified",
              ok: (r.ubos || []).length > 0
            },
            {
              q: "Who runs it?",
              detail: (r.directors || []).map(d => d.name).join(', ') + ((r.authorisedSignatories || []).length > 0 ? ` · Signatories: ${r.authorisedSignatories.map(s => s.name).join(', ')}` : ''),
              ok: (r.directors || []).length > 0
            },
            {
              q: "How does the company make its money?",
              detail: r.sofAssessment?.score === 'Satisfactory' ? (r.companyDetails?.businessDescription || 'Business model documented — see report').slice(0, 120) : `${r.sofAssessment?.score || 'Not assessed'} — ${r.sofAssessment?.narrative?.slice(0,100) || 'see report'}`,
              ok: !!(r.sofAssessment?.score && r.sofAssessment.score !== 'Unsatisfactory')
            },
            {
              q: "Where are funds coming from?",
              detail: r.sofAssessment?.namedAccount ? `${r.sofAssessment.namedAccount}${r.sofAssessment.fundingRoute ? ' · ' + r.sofAssessment.fundingRoute.slice(0,80) : ''}` : "Not confirmed",
              ok: !!(r.sofAssessment?.namedAccount)
            }
          ];

          const hasW = walletScreenings.length > 0;
          const tr = r.travelRule;
          const ubos25 = (r.ubos || []).filter(u => parseFloat(String(u.ownership).match(/[\d.]+/)?.[0] || '0') >= 25);

          const procedural = [
            {
              q: "Identity verified (SumSub)",
              detail: sumsubOk ? `${verifications.filter(v => v.status === 'approved').length} of ${verifications.length} individuals verified` : verifications.length > 0 ? `${verifications.filter(v => v.status === 'approved').length} of ${verifications.length} verified — some pending` : "Not yet completed",
              ok: sumsubOk
            },
            {
              q: "Wallet screening (Chainalysis)",
              detail: hasW ? `${walletScreenings.length} wallet(s) screened — ${walletScreenings.map(w => `${w.risk_level || 'Unknown'}`).join(', ')}` : "No crypto wallets declared",
              ok: !hasW || walletScreenings.length > 0
            },
            {
              q: "Travel Rule (FINMA 02/2019)",
              detail: !tr || walletScreenings.length === 0 ? "No crypto wallets — not applicable" : tr.overallStatus === "compliant" ? "All wallets verified" : `${(tr.wallets || []).filter(w => w.travelRuleStatus !== "compliant").length} wallet(s) pending`,
              ok: !tr || walletScreenings.length === 0 || tr.overallStatus === "compliant"
            },
            {
              q: "UBO Source of Wealth interview",
              detail: r.sowWaiver ? `Not required — ${toStr(r.sowWaiver.reason)} (MLRO waiver)` : ubos25.length === 0 ? "No UBOs at 25%+ threshold" : ubos25.every(u => u.sowAssessment || u.sowInterviewComplete) ? `${ubos25.map(u => u.name).join(', ')} — interviewed and assessed` : `${ubos25.filter(u => !u.sowAssessment && !u.sowInterviewComplete).map(u => u.name).join(', ')} — pending`,
              ok: !!r.sowWaiver || ubos25.length === 0 || ubos25.every(u => u.sowAssessment || u.sowInterviewComplete)
            }
          ];

          const allOk = sixQuestions.every(c => c.ok) && procedural.every(p => p.ok);
          const totalDone = [...sixQuestions, ...procedural].filter(c => c.ok).length;
          const total = sixQuestions.length + procedural.length;

          return (
            <div style={{ ...S.section, marginBottom: 20 }}>
              <div style={S.sectionTitle}>What the MLRO has established</div>
              <div style={{ ...S.para, marginBottom: 14 }}>
                The following summarises how the MLRO has addressed the six key questions required under VQF/AMLA, together with the four mandatory procedural requirements. All items were completed and documented prior to Director approval and this submission.
              </div>
              <div style={{ background: allOk ? "#f0fdf4" : "#fff7ed", border: `1px solid ${allOk ? "#86efac" : "#fed7aa"}`, borderRadius: 8, padding: "10px 14px", marginBottom: 14 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: allOk ? "#15803d" : "#d97706", fontFamily: "Arial, sans-serif" }}>
                  {allOk ? `✓ All ${total} items addressed — file is complete` : `⚠ ${totalDone} of ${total} items addressed — see details below`}
                </div>
              </div>

              {/* Six Questions */}
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: "#1e2d5a", textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: 8, fontFamily: "Arial, sans-serif" }}>Six Key Questions</div>
                <table style={{ width: "100%", borderCollapse: "collapse", border: "1px solid #e5e7eb", borderRadius: 8, overflow: "hidden" }}>
                  <thead>
                    <tr style={{ background: "#1e2d5a" }}>
                      <th style={{ padding: "8px 12px", textAlign: "left", fontSize: 11, color: "white", fontFamily: "Arial, sans-serif", fontWeight: 700, width: 220 }}>Question</th>
                      <th style={{ padding: "8px 12px", textAlign: "center", fontSize: 11, color: "white", fontFamily: "Arial, sans-serif", fontWeight: 700, width: 50 }}>Status</th>
                      <th style={{ padding: "8px 12px", textAlign: "left", fontSize: 11, color: "white", fontFamily: "Arial, sans-serif", fontWeight: 700 }}>Evidence / MLRO Finding</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sixQuestions.map((c, i) => (
                      <tr key={i} style={{ background: i % 2 === 0 ? "#fff" : "#f9fafb", borderBottom: "1px solid #f3f4f6" }}>
                        <td style={{ padding: "8px 12px", fontSize: 13, color: "#111827", fontFamily: "Arial, sans-serif", fontWeight: 600 }}>{c.q}</td>
                        <td style={{ padding: "8px 12px", textAlign: "center", fontSize: 16 }}>{c.ok ? "✅" : "⚠️"}</td>
                        <td style={{ padding: "8px 12px", fontSize: 12, color: c.ok ? "#374151" : "#d97706", fontFamily: "Arial, sans-serif" }}>{c.detail}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Procedural */}
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: "#1e2d5a", textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: 8, fontFamily: "Arial, sans-serif" }}>Procedural Requirements</div>
                <table style={{ width: "100%", borderCollapse: "collapse", border: "1px solid #e5e7eb", borderRadius: 8, overflow: "hidden" }}>
                  <thead>
                    <tr style={{ background: "#1e2d5a" }}>
                      <th style={{ padding: "8px 12px", textAlign: "left", fontSize: 11, color: "white", fontFamily: "Arial, sans-serif", fontWeight: 700, width: 220 }}>Requirement</th>
                      <th style={{ padding: "8px 12px", textAlign: "center", fontSize: 11, color: "white", fontFamily: "Arial, sans-serif", fontWeight: 700, width: 50 }}>Status</th>
                      <th style={{ padding: "8px 12px", textAlign: "left", fontSize: 11, color: "white", fontFamily: "Arial, sans-serif", fontWeight: 700 }}>Detail</th>
                    </tr>
                  </thead>
                  <tbody>
                    {procedural.map((c, i) => (
                      <tr key={i} style={{ background: i % 2 === 0 ? "#fff" : "#f9fafb", borderBottom: "1px solid #f3f4f6" }}>
                        <td style={{ padding: "8px 12px", fontSize: 13, color: "#111827", fontFamily: "Arial, sans-serif", fontWeight: 600 }}>{c.q}</td>
                        <td style={{ padding: "8px 12px", textAlign: "center", fontSize: 16 }}>{c.ok ? "✅" : "⚠️"}</td>
                        <td style={{ padding: "8px 12px", fontSize: 12, color: c.ok ? "#374151" : "#d97706", fontFamily: "Arial, sans-serif" }}>{c.detail}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })()}

        {/* Client Profile */}
        <div style={S.section}>
          <div style={S.sectionTitle}>1. Client Profile</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 24px" }}>
            {[
              ["Legal Name", cd.legalName || session?.company_name],
              ["Jurisdiction", cd.jurisdiction || report.jurisdiction],
              ["Registration No.", cd.registrationNumber || report.registrationNumber],
              ["Incorporated", cd.dateOfIncorporation],
              ["Registered Address", cd.registeredAddress || report.registeredAddress],
              ["Business", cd.businessDescription || report.businessDescription],
            ].filter(([,v]) => v).map(([label, val]) => (
              <div key={label} style={S.kv}>
                <span style={S.kvLabel}>{label}</span>
                <span style={S.kvVal}>{toStr(val)}</span>
              </div>
            ))}
          </div>
          {(cd.groupStructure || report.groupStructure) && <div style={{ marginTop: 12, ...S.para }}><strong>Group structure:</strong> {toStr(cd.groupStructure || report.groupStructure)}</div>}
        </div>

        {/* Risk & FinSA */}
        <div style={S.section}>
          <div style={S.sectionTitle}>2. Risk Assessment & FinSA Classification</div>
          <div style={{ display: "flex", gap: 24, marginBottom: 16, flexWrap: "wrap" }}>
            <div style={{ padding: "12px 20px", background: "#f9fafb", borderRadius: 8, border: "1px solid #e5e7eb" }}>
              <div style={{ fontSize: 11, color: "#9ca3af", fontFamily: "Arial, sans-serif", marginBottom: 4 }}>Risk Tier</div>
              <span style={S.badge(riskColor)}>{report.riskTier || "—"}</span>
            </div>
            <div style={{ padding: "12px 20px", background: "#f9fafb", borderRadius: 8, border: "1px solid #e5e7eb" }}>
              <div style={{ fontSize: 11, color: "#9ca3af", fontFamily: "Arial, sans-serif", marginBottom: 4 }}>FinSA Classification</div>
              <span style={S.badge("#1e2d5a")}>{finsaCategory}</span>
            </div>
            <div style={{ padding: "12px 20px", background: "#f9fafb", borderRadius: 8, border: "1px solid #e5e7eb" }}>
              <div style={{ fontSize: 11, color: "#9ca3af", fontFamily: "Arial, sans-serif", marginBottom: 4 }}>Review Period</div>
              <div style={{ fontSize: 13, fontWeight: 600, color: "#111827", fontFamily: "Arial, sans-serif" }}>{report.reviewPeriod || "—"}</div>
            </div>
          </div>
          {finsaBasis && <div style={S.para}><strong>FinSA basis:</strong> {toStr(finsaBasis)}</div>}
          {(typeof fc === "object" && fc.assessment) && <div style={S.para}>{toStr(fc.assessment)}</div>}
          {report.riskRationale && <div style={{ ...S.para, marginTop: 8 }}>{toStr(report.riskRationale)}</div>}
        </div>

        {/* Beneficial Ownership */}
        <div style={S.section}>
          <div style={S.sectionTitle}>3. Beneficial Ownership & Identity Verification</div>
          {(report.ubos || []).map((ubo, i) => {
            const verifMatch = verifFor(ubo.name);
            return (
              <div key={i} style={{ padding: "14px 16px", background: "#f9fafb", borderRadius: 8, marginBottom: 10, border: "1px solid #e5e7eb" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
                  <div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: "#111827", fontFamily: "Arial, sans-serif" }}>{ubo.name}</div>
                    <div style={{ fontSize: 12, color: "#6b7280", fontFamily: "Arial, sans-serif", marginTop: 2 }}>{ubo.ownership} · {ubo.nationality}{ubo.countryOfResidence ? " · Resident: " + ubo.countryOfResidence : ""}</div>
                  </div>
                  <span style={S.badge(parseFloat(ubo.ownership) < 25 ? "#6b7280" : ubo.corporateUBO ? "#7c3aed" : "#1e2d5a")}>{parseFloat(ubo.ownership) < 25 ? "Shareholder (<25%)" : ubo.corporateUBO ? "Corporate UBO" : "Beneficial Owner"}</span>
                </div>
                {/* Identity verification */}
                <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
                  <div style={{ padding: "8px 12px", background: "#fff", borderRadius: 6, border: "1px solid #e5e7eb", flex: 1, minWidth: 160 }}>
                    <div style={{ fontSize: 10, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.5px", fontFamily: "Arial, sans-serif", marginBottom: 4 }}>ID Verification (Sumsub)</div>
                    {verifMatch ? (
                      <div>
                        <span style={S.badge(verifMatch.status === "approved" ? "#15803d" : verifMatch.status === "pending" ? "#d97706" : "#dc2626")}>
                          {verifMatch.status === "approved" ? "✓ Verified" : verifMatch.status === "pending" ? "⏳ Pending" : "✗ " + verifMatch.status}
                        </span>
                        {verifMatch.applicant_id && <div style={{ fontSize: 10, color: "#9ca3af", fontFamily: "Arial, sans-serif", marginTop: 4 }}>Ref: {verifMatch.applicant_id}</div>}
                      </div>
                    ) : (
                      <span style={S.badge("#9ca3af")}>Pending</span>
                    )}
                    {REPORT_STATUSES.includes(verifMatch?.status) && (
                      <button onClick={withPressFeedback(() => openSumsubReport(verifMatch))} style={{ marginTop: 8, padding: "5px 12px", background: "#1e2d5a", color: "#fff", border: "none", borderRadius: 5, fontSize: 11, fontWeight: 600, cursor: "pointer", fontFamily: "Arial, sans-serif" }}>View Sumsub report</button>
                    )}
                  </div>
                  <div style={{ padding: "8px 12px", background: "#fff", borderRadius: 6, border: "1px solid #e5e7eb", flex: 1, minWidth: 160 }}>
                    <div style={{ fontSize: 10, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.5px", fontFamily: "Arial, sans-serif", marginBottom: 4 }}>PEP / Sanctions</div>
                    {(verifMatch?.pep_match || verifMatch?.sanctions_match || verifMatch?.adverse_media) ? (
                      <span>
                        {verifMatch.sanctions_match && <span style={{ ...S.badge("#dc2626"), marginRight: 4 }}>⚠ Sanctions</span>}
                        {verifMatch.pep_match && <span style={{ ...S.badge("#d97706"), marginRight: 4 }}>⚠ PEP</span>}
                        {verifMatch.adverse_media && <span style={S.badge("#d97706")}>⚠ Adverse media</span>}
                      </span>
                    ) : verifMatch?.status === "approved" ? (
                      <span style={S.badge("#15803d")}>✓ Clear</span>
                    ) : (
                      <span style={S.badge("#9ca3af")}>Pending</span>
                    )}
                    {REPORT_STATUSES.includes(verifMatch?.status) && (
                      <button onClick={withPressFeedback(() => openAmlReport(verifMatch, ubo.name, ubo.corporateUBO ? "Corporate UBO" : "Individual UBO"))} style={{ marginTop: 8, padding: "5px 12px", background: "#fff", color: "#1e2d5a", border: "1px solid #1e2d5a", borderRadius: 5, fontSize: 11, fontWeight: 600, cursor: "pointer", fontFamily: "Arial, sans-serif" }}>View AML screening report</button>
                    )}
                  </div>
                  {ubo.controlBeyondOwnership !== undefined && (
                    <div style={{ padding: "8px 12px", background: "#fff", borderRadius: 6, border: "1px solid #e5e7eb", flex: 1, minWidth: 160 }}>
                      <div style={{ fontSize: 10, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.5px", fontFamily: "Arial, sans-serif", marginBottom: 4 }}>Control beyond ownership</div>
                      <span style={S.badge(ubo.controlBeyondOwnership ? "#d97706" : "#15803d")}>{ubo.controlBeyondOwnership ? "Yes" : "No"}</span>
                      {ubo.controlNotes && <div style={{ fontSize: 11, color: "#6b7280", fontFamily: "Arial, sans-serif", marginTop: 4 }}>{toStr(ubo.controlNotes)}</div>}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
          {(() => {
            const people = reqPeople.length > 0 ? reqPeople
              : (report.directors || []).map(d => (typeof d === "object" ? { name: d.name, role: d.role || "Director" } : { name: d, role: "Director" }));
            if (people.length === 0) return null;
            return (
              <div style={{ marginTop: 12 }}>
                <div style={{ fontSize: 11, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 8, fontFamily: "Arial, sans-serif", fontWeight: 700 }}>Directors, signatories &amp; key persons — ID &amp; screening</div>
                {people.map((p, i) => {
                  const v = verifFor(p.name);
                  const ok = v?.status === "approved";
                  const flagged = v && (v.pep_match || v.sanctions_match || v.adverse_media);
                  return (
                    <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "8px 10px", background: "#f9fafb", borderRadius: 6, marginBottom: 4, flexWrap: "wrap" }}>
                      <div style={{ flex: 1, minWidth: 180 }}>
                        <span style={{ fontSize: 13, color: "#111827", fontFamily: "Arial, sans-serif", fontWeight: 500 }}>{toStr(p.name)}</span>
                        <span style={{ fontSize: 12, color: "#6b7280", fontFamily: "Arial, sans-serif", marginLeft: 8 }}>{toStr(p.role)}</span>
                      </div>
                      <span style={S.badge(ok ? "#15803d" : v ? "#d97706" : "#9ca3af")}>{ok ? "✓ Verified" : v ? "⏳ " + (v.status || "Pending") : "Pending"}</span>
                      {flagged ? (
                        <span>
                          {v.sanctions_match && <span style={{ ...S.badge("#dc2626"), marginLeft: 4 }}>⚠ Sanctions</span>}
                          {v.pep_match && <span style={{ ...S.badge("#d97706"), marginLeft: 4 }}>⚠ PEP</span>}
                          {v.adverse_media && <span style={{ ...S.badge("#d97706"), marginLeft: 4 }}>⚠ Adverse media</span>}
                        </span>
                      ) : ok ? <span style={{ ...S.badge("#15803d"), marginLeft: 4 }}>PEP/Sanctions clear</span> : null}
                      {REPORT_STATUSES.includes(v?.status) && (
                        <span style={{ display: "inline-flex", gap: 6, marginLeft: "auto" }}>
                          <button onClick={withPressFeedback(() => openSumsubReport(v))} style={{ padding: "4px 10px", background: "#1e2d5a", color: "#fff", border: "none", borderRadius: 5, fontSize: 11, fontWeight: 600, cursor: "pointer", fontFamily: "Arial, sans-serif" }}>Sumsub report</button>
                          <button onClick={withPressFeedback(() => openAmlReport(v, p.name, p.role))} style={{ padding: "4px 10px", background: "#fff", color: "#1e2d5a", border: "1px solid #1e2d5a", borderRadius: 5, fontSize: 11, fontWeight: 600, cursor: "pointer", fontFamily: "Arial, sans-serif" }}>AML report</button>
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })()}

          {/* Entity sanctions screening — OpenSanctions (always visible, incl. not-run state) */}
          {(() => {
            const st = entitySanctions?.status;
            const d = entitySanctions?.data || {};
            const matches = [...(d.hits || []).map(h => ({ ...h, level: "hit" })), ...(d.cautionMatches || []).map(h => ({ ...h, level: "caution" }))];
            const dismissed = d.dismissed || {};
            const head = st === "hit" ? ["#dc2626", "⚠ Sanctions hit"] : st === "caution" ? ["#d97706", "◈ Partial match"]
              : (st === "clear" || st === "clear_with_notes") ? ["#15803d", st === "clear_with_notes" ? "✓ Clear (false positives noted)" : "✓ Clear"]
              : ["#9ca3af", st === "running" ? "Screening…" : "Not yet run"];
            return (
              <div style={{ marginTop: 14, paddingTop: 14, borderTop: "1px solid #e5e7eb" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8, flexWrap: "wrap", gap: 8 }}>
                  <div>
                    <div style={{ fontSize: 11, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.5px", fontFamily: "Arial, sans-serif", fontWeight: 700 }}>Entity Sanctions Screening — OpenSanctions</div>
                    <div style={{ fontSize: 11, color: "#9ca3af", fontFamily: "Arial, sans-serif", marginTop: 2 }}>OFAC · EU · UK HMT · UN · Swiss SECO · 200+ lists · company entity (individuals screened via Sumsub above)</div>
                  </div>
                  <span style={S.badge(head[0])}>{head[1]}</span>
                </div>
                {(st === "clear" || st === "clear_with_notes") && (
                  <div style={{ fontSize: 12, color: "#374151", fontFamily: "Arial, sans-serif", lineHeight: 1.6 }}>
                    No sanctions match for <strong>{toStr(d.entityChecked) || toStr(cd.legalName)}</strong>{d.listsSearched ? " · " + toStr(d.listsSearched) : ""}{d.checkedAt ? " · screened " + new Date(d.checkedAt).toLocaleDateString("en-GB") : ""}.
                  </div>
                )}
                {(st === "hit" || st === "caution") && matches.map((h, i) => {
                  const dis = dismissed[h.name];
                  return (
                    <div key={i} style={{ background: dis ? "#f0fdf4" : h.level === "hit" ? "#fff1f2" : "#fff7ed", border: "1px solid " + (dis ? "#bbf7d0" : h.level === "hit" ? "#fecaca" : "#fed7aa"), borderRadius: 6, padding: "8px 12px", marginBottom: 6 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: dis ? "#6b7280" : h.level === "hit" ? "#991b1b" : "#92400e", textDecoration: dis ? "line-through" : "none", fontFamily: "Arial, sans-serif" }}>{toStr(h.name)} — match {h.score}%</div>
                      <div style={{ fontSize: 11, color: "#6b7280", fontFamily: "Arial, sans-serif" }}>Lists: {(h.datasets || []).join(", ")}{h.countries ? " · " + (Array.isArray(h.countries) ? h.countries.join(", ") : h.countries) : ""}</div>
                      {dis && <div style={{ fontSize: 11, color: "#15803d", fontFamily: "Arial, sans-serif", marginTop: 4, paddingLeft: 8, borderLeft: "2px solid #bbf7d0" }}>✓ False positive — {toStr(dis.dismissedBy)} {dis.dismissedAt ? new Date(dis.dismissedAt).toLocaleDateString("en-GB") : ""}: {toStr(dis.reason)}</div>}
                    </div>
                  );
                })}
                {additionalEntityScreenings.length > 0 && (
                  <div style={{ marginTop: 10 }}>
                    <div style={{ fontSize: 10, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.5px", fontFamily: "Arial, sans-serif", fontWeight: 700, marginBottom: 6 }}>Additional Entities Screened (counterparties / related parties)</div>
                    {additionalEntityScreenings.map((a, ai) => {
                      const aPill = a.status === "hit" ? ["#dc2626", "⚠ Sanctions hit"] : a.status === "caution" ? ["#d97706", "◈ Partial match"]
                        : a.status === "clear_with_notes" ? ["#15803d", "✓ Clear (notes)"] : a.status === "clear" ? ["#15803d", "✓ Clear"] : ["#9ca3af", a.status || "—"];
                      return (
                        <div key={ai} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, padding: "6px 10px", background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 6, marginBottom: 5 }}>
                          <div style={{ minWidth: 0 }}>
                            <span style={{ fontSize: 12, fontWeight: 600, color: "#111827", fontFamily: "Arial, sans-serif" }}>{toStr(a.name)}</span>
                            <span style={{ fontSize: 10, color: "#9ca3af", fontFamily: "Arial, sans-serif", marginLeft: 8 }}>{a.data?.listsSearched ? toStr(a.data.listsSearched) : "OpenSanctions"}{a.checkedAt ? " · " + new Date(a.checkedAt).toLocaleDateString("en-GB") : ""}</span>
                          </div>
                          <span style={S.badge(aPill[0])}>{aPill[1]}</span>
                        </div>
                      );
                    })}
                    {additionalEntityScreenings.some(a => a.data?.dismissed && Object.keys(a.data.dismissed).length > 0) && additionalEntityScreenings.map((a, ai) => {
                      const dism = a.data?.dismissed || {};
                      return Object.entries(dism).map(([hitName, d], di) => (
                        <div key={ai + "-" + di} style={{ fontSize: 11, color: "#6b7280", fontFamily: "Arial, sans-serif", padding: "4px 10px", marginBottom: 3, background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 6 }}>
                          <span style={{ textDecoration: "line-through" }}>{toStr(a.name)}: {toStr(hitName)}</span>
                          <span style={{ color: "#15803d" }}> — false positive confirmed by {toStr(d.dismissedBy)}{d.dismissedAt ? ", " + new Date(d.dismissedAt).toLocaleDateString("en-GB") : ""}: {toStr(d.reason)}</span>
                        </div>
                      ));
                    })}
                  </div>
                )}
              </div>
            );
          })()}
        </div>

        {/* Source of Funds */}
        {report.sofAssessment && (
          <div style={S.section}>
            <div style={S.sectionTitle}>4. Source of Funds</div>
            {report.sofAssessment.fundingRoute && <div style={S.kv}><span style={S.kvLabel}>Funding Route</span><span style={S.kvVal}>{toStr(report.sofAssessment.fundingRoute)}</span></div>}
            {report.sofAssessment.expectedVolumes && <div style={S.kv}><span style={S.kvLabel}>Expected Volumes</span><span style={S.kvVal}>{toStr(report.sofAssessment.expectedVolumes)}</span></div>}
            {report.sofAssessment.assessment && <div style={{ ...S.para, marginTop: 8 }}>{toStr(report.sofAssessment.assessment)}</div>}
          </div>
        )}

        {/* Source of Wealth — unified section 5 */}
        {((report.ubos || []).some(u => parseFloat(u.ownership) >= 25) || sowSessions.length > 0) && (
          <div style={S.section}>
            <div style={S.sectionTitle}>5. Source of Wealth — UBOs ≥ 25%</div>
            <div style={{ ...S.para, color: "#6b7280", marginBottom: 16 }}>
              Source of wealth profiles were collected via a structured compliance interview. Each profile was reviewed and signed off by the internal MLRO.
            </div>
            {(report.ubos || []).filter(u => parseFloat(u.ownership) >= 25).map((ubo, i) => {
              const sowSess = sowSessions.find(s => 
                s.contact_email?.toLowerCase() === ubo.email?.toLowerCase() || 
                s.contact_name?.toLowerCase().includes(ubo.name?.split(" ")[0]?.toLowerCase())
              );
              const sowReport = sowSess?.report || {};
              const isExpanded = expandedSow[ubo.name] || false;
              const assessment = ubo.sowAssessment || sowReport.sowAssessment;
              return (
                <div key={i} style={{ border: "1px solid #e5e7eb", borderRadius: 8, marginBottom: 12, overflow: "hidden" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 16px", background: "#f9fafb", cursor: "pointer" }}
                    onClick={() => setExpandedSow(prev => ({ ...prev, [ubo.name]: !prev[ubo.name] }))}>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: "#111827", fontFamily: "Arial, sans-serif" }}>{ubo.name}</div>
                      <div style={{ fontSize: 12, color: "#6b7280", fontFamily: "Arial, sans-serif", marginTop: 2 }}>{ubo.ownership} · {ubo.nationality}</div>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      {assessment && <span style={S.badge(assessment.includes("Satisfactory") ? "#15803d" : assessment.includes("Basic") || assessment.includes("Clarification") ? "#d97706" : "#dc2626")}>{assessment}</span>}
                      <span style={{ fontSize: 16, color: "#9ca3af" }}>{isExpanded ? "▲" : "▼"}</span>
                    </div>
                  </div>
                  {isExpanded && (
                    <div style={{ padding: "16px 20px" }}>
                      {/* MLRO summary narrative */}
                      {ubo.sowNarrative && (
                        <div style={{ marginBottom: 14 }}>
                          <div style={{ fontSize: 11, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.5px", fontFamily: "Arial, sans-serif", marginBottom: 6, fontWeight: 700 }}>MLRO Summary</div>
                          <div style={S.para}>{toStr(ubo.sowNarrative)}</div>
                          {ubo.sowMlroRationale && <div style={{ ...S.para, fontStyle: "italic", color: "#6b7280", marginTop: 4, paddingLeft: 12, borderLeft: "3px solid #e5e7eb" }}>{toStr(ubo.sowMlroRationale)}</div>}
                        </div>
                      )}
                      {/* Full interview detail */}
                      {sowSess && (
                        <div>
                          {sowReport.professionalBackground && (
                            <div style={{ marginBottom: 12 }}>
                              <div style={{ fontSize: 11, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.5px", fontFamily: "Arial, sans-serif", marginBottom: 5, fontWeight: 700 }}>Professional Background</div>
                              <div style={S.para}>{toStr(sowReport.professionalBackground)}</div>
                            </div>
                          )}
                          {sowReport.shareholdingOrigin && (
                            <div style={{ marginBottom: 12 }}>
                              <div style={{ fontSize: 11, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.5px", fontFamily: "Arial, sans-serif", marginBottom: 5, fontWeight: 700 }}>Shareholding Origin</div>
                              <div style={S.para}>{toStr(sowReport.shareholdingOrigin)}</div>
                            </div>
                          )}
                          {(sowReport.sowNotes || sowReport.wealthOrigins) && (
                            <div style={{ marginBottom: 12 }}>
                              <div style={{ fontSize: 11, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.5px", fontFamily: "Arial, sans-serif", marginBottom: 5, fontWeight: 700 }}>Wealth Origins</div>
                              <div style={S.para}>{toStr(sowReport.sowNotes || sowReport.wealthOrigins)}</div>
                            </div>
                          )}
                          {sowReport.significantEvents && (
                            <div style={{ marginBottom: 12 }}>
                              <div style={{ fontSize: 11, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.5px", fontFamily: "Arial, sans-serif", marginBottom: 5, fontWeight: 700 }}>Significant Events</div>
                              <div style={S.para}>{toStr(sowReport.significantEvents)}</div>
                            </div>
                          )}
                          {[["Wealth timeframe", sowReport.wealthTimeframe], ["Wealth structure", sowReport.wealthStructure]].filter(([,v]) => v).map(([label, val]) => (
                            <div key={label} style={S.kv}><span style={S.kvLabel}>{label}</span><span style={S.kvVal}>{toStr(val)}</span></div>
                          ))}
                        </div>
                      )}
                      {/* Evidence */}
                      {(ubo.evidenceProvided || sowReport.evidenceProvided) && (
                        <div style={{ padding: "10px 14px", background: "#f0f9ff", border: "1px solid #bae6fd", borderRadius: 6, margin: "12px 0" }}>
                          <div style={{ fontSize: 11, color: "#0369a1", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", fontFamily: "Arial, sans-serif", marginBottom: 4 }}>Evidence Provided</div>
                          <div style={{ fontSize: 13, color: "#0c4a6e", fontFamily: "Arial, sans-serif", lineHeight: 1.6 }}>{toStr(ubo.evidenceProvided || sowReport.evidenceProvided)}</div>
                        </div>
                      )}
                      {/* SOW documents */}
                      {sowSess && (sowDocs[sowSess.id] || []).length > 0 && (
                        <div style={{ marginTop: 12 }}>
                          <div style={{ fontSize: 11, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.5px", fontFamily: "Arial, sans-serif", marginBottom: 8, fontWeight: 700 }}>Supporting Documents</div>
                          {sowDocs[sowSess.id].map((doc, di) => (
                            <div key={di} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 12px", background: "#f9fafb", borderRadius: 6, marginBottom: 6, border: "1px solid #e5e7eb" }}>
                              <span style={{ fontSize: 18 }}>📄</span>
                              <div style={{ flex: 1 }}>
                                <div style={{ fontSize: 13, color: "#111827", fontFamily: "Arial, sans-serif", fontWeight: 500 }}>{doc.document_name}</div>
                                {doc.assessment && <div style={{ fontSize: 11, color: doc.assessment === "Satisfactory" ? "#15803d" : "#d97706", fontFamily: "Arial, sans-serif", marginTop: 2, fontWeight: 600 }}>{doc.assessment}</div>}
                              </div>
                              {doc.file_path ? (
                                <button onClick={async () => {
                                  const { data } = await supabase.storage.from("onboarding-documents").createSignedUrl(doc.file_path, 3600);
                                  if (data?.signedUrl) window.open(data.signedUrl, "_blank");
                                }} style={{ padding: "6px 14px", background: "#1e2d5a", color: "#fff", border: "none", borderRadius: 5, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "Arial, sans-serif" }}>View ↗</button>
                              ) : <span style={{ fontSize: 11, color: "#d1d5db" }}>No file</span>}
                            </div>
                          ))}
                        </div>
                      )}
                      {sowSess?.updated_at && <div style={{ marginTop: 10, fontSize: 11, color: "#9ca3af", fontFamily: "Arial, sans-serif" }}>Interview completed: {new Date(sowSess.updated_at).toLocaleDateString("en-GB")}</div>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

                {/* Tax Compliance */}
        <div style={S.section}>
          <div style={S.sectionTitle}>6. Tax Compliance Declaration</div>
          {report.taxCompliance?.riskIndicatorsPresent ? (
            <div style={{ padding:"10px 14px", background:"#fff7ed", border:"1px solid #fed7aa", borderRadius:6 }}>
              <div style={{ fontSize:12, fontWeight:700, color:"#d97706", fontFamily:"Arial, sans-serif", marginBottom:4 }}>⚠ Risk Indicators Present</div>
              <div style={{ fontSize:12, color:"#92400e", fontFamily:"Arial, sans-serif", lineHeight:1.6 }}>{toStr(report.taxCompliance.notes)}</div>
              {(report.taxCompliance.riskIndicators || []).map((r,i) => <div key={i} style={{ fontSize:11, color:"#92400e", fontFamily:"Arial, sans-serif" }}>• {toStr(r)}</div>)}
            </div>
          ) : report.taxCompliance?.declared ? (
            <div style={{ padding:"10px 14px", background:"#f0fdf4", border:"1px solid #86efac", borderRadius:6 }}>
              <div style={{ fontSize:12, fontWeight:700, color:"#15803d", fontFamily:"Arial, sans-serif", marginBottom:4 }}>✓ Declaration Obtained</div>
              <div style={{ fontSize:12, color:"#166534", fontFamily:"Arial, sans-serif", lineHeight:1.6 }}>{report.taxCompliance.declarationText || "Client confirmed assets are fully tax compliant in all relevant jurisdictions."}</div>
            </div>
          ) : (
            <div style={{ ...S.para, color:"#9ca3af" }}>Tax compliance declaration not yet recorded.</div>
          )}
          <div style={{ fontSize:11, color:"#9ca3af", fontFamily:"Arial, sans-serif", marginTop:8 }}>Required under Swiss AMLA art. 305bis / VQF AML Policy s5.5</div>
        </div>

        {/* Documents */}
        <div style={S.section}>
          <div style={S.sectionTitle}>7. Documents on File ({documents.length})</div>
          {documents.length === 0 ? <div style={{ ...S.para, color: "#9ca3af" }}>No documents uploaded</div> : (
            <div>
              {documents.map((doc, i) => (
                <div key={i} style={{ padding:"12px 14px", borderRadius:6, border:"1px solid #e5e7eb", marginBottom:8, background: i % 2 === 0 ? "#fff" : "#fafafa" }}>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:12 }}>
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:13, fontWeight:600, color:"#111827", fontFamily:"Arial, sans-serif", marginBottom:2 }}>{doc.document_name}</div>
                      {doc.document_type && <div style={{ fontSize:11, color:"#9ca3af", fontFamily:"Arial, sans-serif", marginBottom: doc.analysis ? 6 : 0 }}>{doc.document_type}</div>}
                      {(() => {
                        // Parse analysis whether stored as object or JSON string
                        let a = doc.analysis;
                        if (typeof a === "string") { try { a = JSON.parse(a); } catch(e) { a = null; } }
                        if (!a) return null;
                        const findings = a.keyFindings || [];
                        const materialFlags = [...(a.materialFlags||[]), ...(a.redFlags||[])];
                        const notedFlags = a.notedFlags || [];
                        const informationals = a.informationals || [];
                        return (
                          <div style={{ paddingLeft:8, borderLeft:"3px solid #e5e7eb" }}>
                            {findings.length > 0 && findings.map((f, fi) => (
                              <div key={fi} style={{ fontSize:12, color:"#374151", fontFamily:"Arial, sans-serif", lineHeight:1.6, marginBottom:2 }}>— {toStr(f)}</div>
                            ))}
                            {/* Material & Noted flag callouts intentionally not shown to the external reviewer (aligns with the main report). */}
                            {/* Informationals — blue, factual context only */}
                            {informationals.length > 0 && (
                              <div style={{ marginTop:6, padding:"8px 10px", background:"#eff6ff", border:"1px solid #bfdbfe", borderRadius:4 }}>
                                <div style={{ fontSize:10, color:"#1e40af", fontWeight:700, textTransform:"uppercase", letterSpacing:"0.5px", fontFamily:"Arial, sans-serif", marginBottom:4 }}>ℹ️ Informational</div>
                                {informationals.map((f, fi) => (
                                  <div key={fi} style={{ fontSize:12, color:"#1e3a8a", fontFamily:"Arial, sans-serif", lineHeight:1.6 }}>• {toStr(f)}</div>
                                ))}
                              </div>
                            )}
                            {/* MLRO mitigation note — always shown when present, always retained in audit trail */}
                            {doc.mlro_note && (
                              <div style={{ marginTop:6, padding:"8px 10px", background:"#f0fdf4", border:"1px solid #bbf7d0", borderRadius:4 }}>
                                <div style={{ fontSize:10, color:"#166534", fontWeight:700, textTransform:"uppercase", letterSpacing:"0.5px", fontFamily:"Arial, sans-serif", marginBottom:3 }}>✓ MLRO Mitigation Note</div>
                                <div style={{ fontSize:12, color:"#14532d", fontFamily:"Arial, sans-serif", lineHeight:1.5 }}>{doc.mlro_note}</div>
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                    <div style={{ display:"flex", flexDirection:"column", alignItems:"flex-end", gap:6, flexShrink:0 }}>
                      <span style={S.badge(doc.assessment === "Satisfactory" ? "#15803d" : doc.assessment === "Requires Clarification" ? "#d97706" : "#6b7280")}>
                        {doc.assessment || "Received"}
                      </span>
                      {doc.file_path ? (
                        <button onClick={() => getSignedUrl(doc.file_path)} style={{ fontSize:11, color:"#1e2d5a", background:"transparent", border:"1px solid #1e2d5a", borderRadius:4, padding:"3px 10px", cursor:"pointer", fontFamily:"Arial, sans-serif" }}>View</button>
                      ) : <span style={{ fontSize:11, color:"#d1d5db" }}>—</span>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* MLRO Sign-off statement */}
        <div style={S.section}>
          <div style={S.sectionTitle}>8. MLRO Internal Review &amp; Sign-off</div>
          <div style={{ padding:"14px 16px", background:"#f0fdf4", border:"1px solid #bbf7d0", borderRadius:8 }}>
            <div style={{ fontSize:13, color:"#14532d", fontFamily:"Arial, sans-serif", lineHeight:1.7 }}>
              This file has been reviewed by the internal MLRO at Sage Capital Management. All risk factors identified during the onboarding process have been assessed, mitigated where appropriate, and signed off internally prior to submission for external compliance review. The MLRO's conditions and full case note are set out at the top of this file.
            </div>
            <div style={{ marginTop:10, fontSize:12, color:"#166534", fontFamily:"Arial, sans-serif" }}>
              Approved by: {session?.mlro_decision ? session.mlro_decision + " — " : ""}{session?.approved_at ? new Date(session.approved_at).toLocaleDateString("en-GB", { day:"numeric", month:"long", year:"numeric" }) : "—"}
            </div>
          </div>
        </div>

        {/* Travel Rule Detail */}
        {report.travelRule?.wallets?.length > 0 && (
          <div style={S.section}>
            <div style={S.sectionTitle}>9. Travel Rule — FINMA Guidance 02/2019</div>
            <div style={{ ...S.para, color:"#6b7280", marginBottom:14 }}>
              The following table documents Travel Rule compliance for each virtual asset wallet associated with this client, as required under Swiss FINMA Guidance 02/2019 and VQF art. 13-14.
            </div>
            <table style={{ width:"100%", borderCollapse:"collapse", border:"1px solid #e5e7eb", borderRadius:8, overflow:"hidden" }}>
              <thead>
                <tr style={{ background:"#1e2d5a" }}>
                  {["Wallet Address","Type","Exchange / Method","Status"].map(h => (
                    <th key={h} style={{ padding:"8px 10px", textAlign:"left", fontSize:11, color:"white", fontFamily:"Arial, sans-serif", fontWeight:700 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {report.travelRule.wallets.map((w, i) => (
                  <tr key={i} style={{ background: i%2===0 ? "#fff" : "#f9fafb", borderBottom:"1px solid #f3f4f6" }}>
                    <td style={{ padding:"8px 10px", fontSize:11, color:"#374151", fontFamily:"monospace" }}>{w.address?.slice(0,16)}...{w.address?.slice(-6)}</td>
                    <td style={{ padding:"8px 10px", fontSize:12, color:"#374151", fontFamily:"Arial, sans-serif", textTransform:"capitalize" }}>{w.type || "—"}</td>
                    <td style={{ padding:"8px 10px", fontSize:12, color:"#374151", fontFamily:"Arial, sans-serif" }}>
                      {w.type === "exchange" ? `${w.exchangeName || "—"} ${w.exchangeRegulated ? "· Regulated" : "· Unregulated"} ${w.accountInEntityName ? "· Account verified" : ""}` :
                       w.type === "private" ? `Proof of control: ${w.proofOfControl?.method === "satoshi_test_complete" ? `Satoshi test verified ${w.proofOfControl.verifiedDate ? new Date(w.proofOfControl.verifiedDate).toLocaleDateString("en-GB") : ""}` : "Pending"}` : "—"}
                    </td>
                    <td style={{ padding:"8px 10px" }}>
                      <span style={S.badge(w.travelRuleStatus === "compliant" ? "#15803d" : "#d97706")}>
                        {w.travelRuleStatus === "compliant" ? "✓ Compliant" : w.travelRuleStatus === "pending_satoshi_test" ? "Satoshi pending" : w.travelRuleStatus === "pending_client_response" ? "Awaiting client" : "Pending"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        </>)}

        {/* Ask a Question Panel — Jaybee command interface */}
        {session && (
          <div style={{ ...S.section, border:"1px solid #dbeafe", background:"#f0f9ff" }}>
            <div style={{ ...S.sectionTitle, color:"#1d4ed8" }}>Ask a Question About This File</div>
            <div style={{ fontSize:12, color:"#6b7280", fontFamily:"Arial, sans-serif", marginBottom:12, lineHeight:1.6 }}>
              Ask anything about this client file — the AI has read the complete dossier and can answer specific questions about source of funds, screening results, ownership structure, or any other aspect.
            </div>
            {jayCommandHistory.length > 0 && (
              <div style={{ marginBottom:14, background:"#fff", border:"1px solid #e5e7eb", borderRadius:8, padding:"12px 14px", maxHeight:280, overflowY:"auto" }}>
                {jayCommandHistory.map((msg, i) => (
                  <div key={i} style={{ marginBottom:10 }}>
                    {msg.role === 'user' ? (
                      <div style={{ fontSize:13, fontFamily:"Arial, sans-serif", color:"#1d4ed8", fontWeight:600, marginBottom:2 }}>You: {msg.content}</div>
                    ) : (
                      <div style={{ fontSize:13, fontFamily:"Arial, sans-serif", color:"#374151", lineHeight:1.7, paddingLeft:12, borderLeft:"3px solid #dbeafe", whiteSpace:"pre-wrap" }}>{msg.content}</div>
                    )}
                  </div>
                ))}
                {jayCommandLoading && <div style={{ fontSize:12, color:"#6b7280", fontFamily:"Arial, sans-serif" }}>Reading the file...</div>}
              </div>
            )}
            <div style={{ display:"flex", gap:8 }}>
              <input
                value={jayCommandInput}
                onChange={e => setJayCommandInput(e.target.value)}
                onKeyDown={async e => {
                  if (e.key !== 'Enter' || !jayCommandInput.trim() || jayCommandLoading) return;
                  const cmd = jayCommandInput.trim();
                  setJayCommandInput('');
                  setJayCommandHistory(prev => [...prev, { role:'user', content: cmd }]);
                  setJayCommandLoading(true);
                  try {
                    const res = await fetch('/api/command-interface', {
                      method:'POST', headers:{'Content-Type':'application/json'},
                      body: JSON.stringify({ sessionId: session.id, command: cmd, userRole:'jaybee', conversationHistory: jayCommandHistory })
                    });
                    const data = await res.json();
                    setJayCommandHistory(prev => [...prev, { role:'assistant', content: data.success ? data.answer : 'Error: ' + (data.error || 'Unknown') }]);
                  } catch(err) { setJayCommandHistory(prev => [...prev, { role:'assistant', content:'Connection error.' }]); }
                  setJayCommandLoading(false);
                }}
                placeholder='e.g. "What is the source of the funds shown in the bank statement?" — press Enter'
                style={{ flex:1, padding:"9px 14px", border:"1px solid #d1d5db", borderRadius:6, fontSize:13, fontFamily:"Arial, sans-serif", outline:"none" }}
              />
            </div>
          </div>
        )}

        {/* Decision Panel */}
        <div style={{ ...S.section, border: "2px solid #1e2d5a" }}>
          <div style={S.sectionTitle}>Jaybee Compliance — Decision</div>
          <div style={{ ...S.para, marginBottom: 16 }}>
            Having reviewed the above client file prepared by Sage Capital Management's Compliance Manager, please record your decision below. Your decision will be immediately logged and Sage Capital notified.
          </div>

          {caseNotes.length > 0 && (
            <button onClick={() => setShowNotes(true)} style={{ ...S.btn("#1e2d5a", "#fff"), width: "100%", marginBottom: 16, padding: "12px 16px" }}>
              📋 View MLRO Case Notes ({caseNotes.length})
            </button>
          )}

          <div style={{ padding:"12px 16px", background:"#f0f9ff", border:"1px solid #bae6fd", borderRadius:8, marginBottom:20 }}>
            <div style={{ fontSize:12, color:"#0369a1", fontFamily:"Arial, sans-serif", lineHeight:1.7 }}>
              <strong>Please note:</strong> Recording your approval here does <strong>not</strong> automatically send a welcome email to the client. Your approval is confirmation of your sign-off on this file. Sage Capital Management will send the client their welcome email manually once all final checks have been completed, including receipt of signed VQF Forms A and K.
            </div>
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={{ display: "block", fontSize: 12, color: "#6b7280", fontFamily: "Arial, sans-serif", marginBottom: 6 }}>Your name *</label>
            <input value={decidedBy} onChange={e => setDecidedBy(e.target.value)} placeholder="e.g. Jean-Pierre Dubois, Jaybee Compliance" style={{ width: "100%", padding: "10px 14px", border: "1px solid #d1d5db", borderRadius: 6, fontSize: 13, fontFamily: "Arial, sans-serif", outline: "none" }} />
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={{ display: "block", fontSize: 12, color: "#6b7280", fontFamily: "Arial, sans-serif", marginBottom: 6 }}>Notes / Conditions (required if requesting information or declining)</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Add any conditions, comments, or reasons for your decision..." style={{ width: "100%", padding: "10px 14px", border: "1px solid #d1d5db", borderRadius: 6, fontSize: 13, fontFamily: "Arial, sans-serif", resize: "vertical", minHeight: 80, outline: "none" }} />
          </div>

          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            <button onClick={() => submitDecision("approve")} disabled={submitting} style={{ ...S.btn("#15803d", "#fff"), opacity: submitting ? 0.6 : 1 }}>
              {submitting ? "Submitting..." : "✓ Approve"}
            </button>
            <button onClick={() => submitDecision("info")} disabled={submitting} style={{ ...S.btn("#d97706", "#fff"), opacity: submitting ? 0.6 : 1 }}>
              Request More Information
            </button>
            <button onClick={() => submitDecision("decline")} disabled={submitting} style={{ ...S.btn("#dc2626", "#fff"), opacity: submitting ? 0.6 : 1 }}>
              Decline
            </button>
          </div>
        </div>

        {/* Case notes popup */}
        {showNotes && (
          <div onClick={() => setShowNotes(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 20 }}>
            <div onClick={e => e.stopPropagation()} style={{ background: "#fff", borderRadius: 12, maxWidth: 760, width: "100%", maxHeight: "85vh", overflowY: "auto", padding: "22px 26px", boxShadow: "0 12px 48px rgba(0,0,0,0.35)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, paddingBottom: 10, borderBottom: "2px solid #1e2d5a" }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#1e2d5a", fontFamily: "Arial, sans-serif", textTransform: "uppercase", letterSpacing: "0.6px" }}>MLRO Case Notes ({caseNotes.length})</div>
                <button onClick={() => setShowNotes(false)} style={{ fontSize: 24, lineHeight: 1, color: "#6b7280", background: "transparent", border: "none", cursor: "pointer", fontFamily: "Arial, sans-serif" }}>×</button>
              </div>
              {caseNotes.map((n, i) => (
                <div key={i} style={{ padding: "10px 14px", background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 6, marginBottom: 8 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: "#1e2d5a", fontFamily: "Arial, sans-serif" }}>{toStr(n.author) || "MLRO"}</span>
                    <span style={{ fontSize: 11, color: "#9ca3af", fontFamily: "Arial, sans-serif" }}>{n.created_at ? new Date(n.created_at).toLocaleString("en-GB") : ""}</span>
                  </div>
                  <div style={{ fontSize: 13, color: "#374151", fontFamily: "Arial, sans-serif" }}>{formatNotes(toStr(n.content))}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div style={{ textAlign: "center", padding: "16px", fontFamily: "Arial, sans-serif", fontSize: 11, color: "#9ca3af" }}>
          Confidential — prepared for Jaybee Compliance by Sage Capital Management · {new Date().getFullYear()}
        </div>
      </div>

      {/* Form A/K confirmed declaration modal (Sumsub margin-48 route) */}
      {formConfirmView && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(17,24,39,0.55)", zIndex: 300, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }} onClick={e => e.target === e.currentTarget && setFormConfirmView(null)}>
          <div style={{ background: "#fff", borderRadius: 10, padding: "26px 30px", width: 560, maxWidth: "94vw", maxHeight: "84vh", overflowY: "auto", fontFamily: "Arial, sans-serif", boxShadow: "0 20px 50px rgba(0,0,0,0.25)" }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: "#1e2d5a", marginBottom: 2, lineHeight: 1.4 }}>{VQF_FORM_TITLES[formConfirmView.record?.form_type] || `Form ${formConfirmView.record?.form_type}`}</div>
            <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 16 }}>{formConfirmView.record?.signer_name} · confirmed electronically via Sumsub questionnaire + phone OTP (FINMA margin 48)</div>

            {formConfirmView.loading && <div style={{ fontSize: 13, color: "#6b7280", padding: "20px 0" }}>Retrieving confirmed declaration…</div>}

            {formConfirmView.error && (
              <div style={{ fontSize: 13, color: "#b91c1c", background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8, padding: "10px 14px" }}>
                The declaration could not be retrieved: {formConfirmView.error}
                <div style={{ marginTop: 6, color: "#6b7280" }}>The confirmation status itself remains valid — the record of confirmation is held on file. Please contact Sage Capital if you need the full declaration content.</div>
              </div>
            )}

            {formConfirmView.data && !formConfirmView.data.found && (
              <div style={{ fontSize: 13, color: "#92400e", background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 8, padding: "10px 14px" }}>
                Sumsub returned no questionnaire content for this confirmation. The confirmation record itself remains on file with Sage Capital.
              </div>
            )}

            {formConfirmView.data?.found && (
              <>
                <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 8, padding: "10px 14px", marginBottom: 16, fontSize: 12, color: "#374151" }}>
                  <div><strong>Confirmed:</strong> {formConfirmView.record?.confirmed_at ? new Date(formConfirmView.record.confirmed_at).toLocaleString("en-GB") : "—"}</div>
                  {formConfirmView.record?.confirmation_ip && <div><strong>IP at confirmation:</strong> {formConfirmView.record.confirmation_ip}</div>}
                  <div><strong>Applicant Action ID:</strong> {formConfirmView.record?.applicant_action_id || "—"}</div>
                  <div style={{ marginTop: 4, paddingTop: 4, borderTop: "1px solid #d1fae5" }}>
                    Phone OTP verification: ✓ Confirmed — this verification level requires both the questionnaire and the phone OTP step to pass before the confirmation shows as complete.
                  </div>
                </div>

                {(() => {
                  const model = buildVqfFormModel({ record: formConfirmView.record, questionnaire: formConfirmView.data.questionnaire, session, report, fieldLabel: sumsubFieldLabel, valueLabel: sumsubValueLabel });
                  return (
                    <>
                      <div style={{ fontSize: 11, color: "#1e2d5a", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 6 }}>Contracting Partner</div>
                      <div style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 8, padding: "10px 14px", marginBottom: 14, fontSize: 12, color: "#374151", lineHeight: 1.7, fontFamily: "Arial, sans-serif" }}>
                        <div><span style={{ color: "#9ca3af" }}>Legal name: </span>{model.contractingPartner.legalName || "—"}</div>
                        <div><span style={{ color: "#9ca3af" }}>Registered address: </span>{model.contractingPartner.address || "—"}</div>
                        <div><span style={{ color: "#9ca3af" }}>Country: </span>{model.contractingPartner.country || "—"}{model.contractingPartner.registrationNumber ? " · Reg " + model.contractingPartner.registrationNumber : ""}</div>
                      </div>
                      {model.formType === "K" && model.fiduciaryYes && (
                        <div style={{ background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 8, padding: "8px 12px", marginBottom: 14, fontSize: 12, color: "#92400e", fontFamily: "Arial, sans-serif" }}>
                          ⚠ Fiduciary holding of assets declared — a Form A from the third-party beneficial owner applies (fiduciary exception).
                        </div>
                      )}
                      <div style={{ fontSize: 11, color: "#1e2d5a", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 8 }}>Declaration</div>
                      {model.fields.map((f) => (
                        <div key={f.id} style={{ display: "flex", justifyContent: "space-between", gap: 16, fontSize: 13, color: "#111827", padding: "5px 0", borderBottom: "1px solid #f3f4f6" }}>
                          <span style={{ color: "#6b7280" }}>{f.label}</span>
                          <span style={{ textAlign: "right", fontWeight: 600 }}>{f.value}</span>
                        </div>
                      ))}
                      <div style={{ marginTop: 14, textAlign: "right" }}>
                        <button onClick={() => generateFormConfirmationPDF(model)} style={{ fontSize: 12, padding: "6px 16px", borderRadius: 6, border: "none", background: "#1e2d5a", color: "#fff", fontWeight: 700, cursor: "pointer", fontFamily: "Arial, sans-serif" }}>⬇ Download Form {model.formType} (PDF)</button>
                      </div>
                    </>
                  );
                })()}
              </>
            )}

            <div style={{ marginTop: 18, textAlign: "right" }}>
              <button onClick={() => setFormConfirmView(null)} style={{ fontSize: 12, padding: "7px 18px", borderRadius: 6, border: "1px solid #d1d5db", background: "#fff", color: "#374151", cursor: "pointer", fontFamily: "Arial, sans-serif" }}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
